import { ICONS } from './icons.js';
import { UploadError, type Api, type AudioPrepStatus, type YoutubeMatch, type YoutubeVideo } from './api.js';
import { createYoutubePlayer, parseYoutubeId, type YoutubePlayer } from './youtube-clip.js';
import { createPendingCutter, createWaveformTrimmer, type AudioEffects, type WaveformTrimmer } from './waveform.js';
import {
  createMatchPrefetch,
  matchPlan,
  offerLengthNote,
  offerTime,
  offerVisible,
  OFFER_TEXTS,
  sameLength,
  songAnswer,
  songInMix,
  songKey,
  type PickedSong,
} from './song-offer.js';
import type {
  AudioKind,
  AudioOptionConfig,
  AudioProfileDef,
  AudioSelection,
  OptionDef,
} from './types.js';

/**
 * How a shopper gives us the sound for their product.
 *
 * Two parts, because a gift can carry several sounds and a checklist of six
 * open cards was a long, confusing scroll on a phone:
 *
 *   • "Tvoj zvuk" — the arrangement. Every sound added so far, in the order it
 *     will play, how long each is, the pause before it, the total against what
 *     the product holds (and the Premium length past it), and one button that
 *     plays the whole thing exactly as the product will.
 *   • "Dodaj zvuk" — one tile per way of providing sound (a voice message, the
 *     AI voice, YouTube, TikTok, a file, an AI song). A tile opens that one
 *     editor; "Gotovo" puts the sound in the arrangement and closes it again.
 *     Only one editor is ever open.
 *
 * The design rule carried over from the first version still holds: a typed
 * link or name alone is a valid answer. The cutter, the AI, the previews are
 * upgrades — a broken fetch must never block a sale.
 */

const TEXTS = {
  song: 'Napravi svoju pesmu (AI)',
  songDesc: 'Tvoj tekst, tvoj stil',
  voice: 'Snimi glasovnu poruku',
  voiceDesc: 'Tvojim glasom, sa telefona',
  tts: 'AI glasovna poruka',
  ttsDesc: 'Ukucaj tekst, AI ga izgovori',
  youtube: 'Pesma sa YouTube-a',
  youtubeDesc: 'Link ili naziv pesme',
  tiktok: 'Zvuk sa TikTok-a',
  tiktokDesc: 'Nalepi link videa',
  upload: 'Otpremi svoj zvuk',
  uploadDesc: 'MP3, WAV, M4A…',

  // The two parts of the step.
  mixTitle: 'Tvoj zvuk',
  addFirst: 'Izaberi šta će proizvod da pušta',
  addMore: 'Dodaj još zvuka',
  replace: 'Zameni zvuk',
  added: 'Dodato',
  addedCount: (n: number) => `Dodato: ${n}`,
  /** One source only: its editor offers the next one ("+ Još jedna pesma sa YouTube-a"). */
  addAnother: (label: string) => `+ Još jedno: ${label}`,
  full: (n: number) => `Dodato je ${n} zvukova — toliko najviše staje. Ukloni neki da dodaš novi.`,
  back: 'Nazad',
  done: 'Gotovo',
  doneHint: 'Kad dodaš zvuk, pritisni „Gotovo“ — biće u redosledu iznad.',
  edit: 'Izmeni',
  remove: 'Ukloni',
  moveUp: 'Pomeri gore',
  moveDown: 'Pomeri dole',
  pauseBefore: 'pauza',
  lengthUnknown: 'dužina se zna posle izbora',
  countSounds: (n: number) => (n === 1 ? '1 zvuk' : n < 5 ? `${n} zvuka` : `${n} zvukova`),
  included: (max: string) => `Uključeno do ${max}`,
  premiumOffer: (max: string, price: string) => `Premium do ${max} · +${price}`,
  /** Past the included length: said once, quietly — Premium is simply on. */
  premiumOn: (price: string, max: string) => `✨ Premium dužina je uključena (+${price}) — sve zajedno može do ${max}.`,
  /** Near the included length: a word ahead, never a warning. */
  premiumSoon: (left: string, base: string, price: string, max: string) =>
    `Još ${left} do ${base}. Ako dodaš više, automatski prelaziš na Premium dužinu (+${price}, do ${max}).`,
  overLimit: (max: string) => `Predugačko je — proizvod pušta najviše ${max}. Skrati neki deo (✎) ili ukloni zvuk.`,
  unknownNote: 'Pesma data samo nazivom ne ulazi u zbir — mi je sečemo tako da sve stane.',
  /** A TikTok link whose sound is not loaded yet: its length is learnt with it. */
  unknownTiktokNote: 'Dužinu TikTok zvuka saznajemo čim se učita — sečemo ga tako da sve stane.',
  unknownMixedNote: 'Dužinu nekih zvukova saznajemo kasnije — sečemo ih tako da sve stane.',
  playAll: 'Preslušaj sve',
  playOne: 'Preslušaj',
  playStop: 'Zaustavi',
  playSkipped: (title: string) => `„${title}“ ne može da se presluša pre porudžbine — preskočeno.`,
  playTap: 'Telefon traži dodir: dodirni sličicu videa da pesma krene.',

  // Players.
  play: 'Pusti',
  pause: 'Pauziraj',
  seek: 'Mesto u snimku',
  cantPlay: 'Ovaj snimak pregledač ne može da pusti, ali je sačuvan za proizvod.',

  // YouTube.
  linkPlaceholder: 'Nalepi link ili ukucaj naziv pesme…',
  find: 'Pronađi',
  searching: 'Tražimo na YouTube-u…',
  pickResult: 'Izaberi',
  changeSong: 'Promeni pesmu',
  nameOnly: 'Može i samo naziv — mi ćemo pronaći pesmu. Ili klikni „Pronađi“ da je poslušaš i izabereš deo.',
  otherLink: 'Taj link ćemo preuzeti posle porudžbine. Upiši ispod koji deo pesme želiš.',
  ytRateLimited: 'Previše pretraga zaredom — sačekaj malo pa pokušaj ponovo.',
  ytNoResults: 'Nismo našli tu pesmu na YouTube-u — proveri naziv ili nalepi link.',
  ytFailed: 'Pretraga trenutno ne radi. Ostavi naziv pesme — mi ćemo je pronaći za tebe.',
  /** A video its owner will not let play on other sites: still usable, just not heard here yet. */
  ytBlocked: '✓ Pesma je izabrana. Ovde ne može da se pusti, ali je pripremamo — deo možeš da izabereš odmah.',
  /** The quiet line in the cutter while a song that cannot play here is on its way. */
  ytPreparing: 'Pesma stiže za koji trenutak — tada je čuješ i ovde. Deo već možeš da biraš.',
  ytPrepared: '✓ Pesma je učitana — sada možeš da je čuješ i iseckaš.',
  /** Nothing to hear here, and that is fine: said kindly, never as an error. */
  ytPrepFailed:
    '✓ Sve je u redu — pesma je izabrana. Ne možemo odmah da je pustimo ovde, ali je pripremamo i sečemo tačno kako si označio/la.',
  /** The player works; the song's own file (its real wave) is on its way meanwhile — said quietly, never waited for. */
  ytBackground: 'Talas pesme stiže za koji trenutak — deo već možeš da biraš.',
  wholeSong: 'Cela pesma',
  /** The pick is the answer at once: the whole song, unless the shopper asks to cut it. */
  ytWholeDone: '✓ Cela pesma je izabrana — možeš odmah dalje.',
  ytPartDone: '✓ Deo pesme je izabran — možeš odmah dalje.',
  cutPart: 'Iseci deo',
  /** The choice row's name for a screen reader: "Cela pesma ili deo", "Ceo zvuk ili deo". */
  partQuestion: (whole: string) => `${whole} ili deo`,
  /**
   * The sounds together run past what the product plays: said calmly in place
   * of "možeš odmah dalje" (the order would be refused), with what to do.
   */
  overHeadSong: (max: string) => `Cela pesma je duža od ${max}`,
  overHeadTiktok: (max: string) => `Ceo zvuk je duži od ${max}`,
  overHeadMix: (max: string) => `Zajedno je duže od ${max}`,
  overCutSong: 'iseci deo ove pesme.',
  overCutTiktok: 'iseci deo ovog zvuka.',
  overLater: 'deo biraš čim je pronađemo.',
  overChoose: 'izaberi pravu verziju ispod i iseci deo.',
  overWords: 'izaberi „Deo pesme“ ispod i upiši od–do.',
  overOthers: 'skrati neki od ostalih zvukova (✎).',
  ytReady: '✓ Pesma je spremna za proizvod.',
  ytChosen: '✓ Pesma je izabrana.',
  ytTryingOther: 'Tražimo drugu verziju iste pesme…',
  ytAlternatives: 'Ili izaberi verziju iste pesme koja se pušta ovde:',
  usePickedSong: 'Koristi izabranu pesmu',
  from: 'Od',
  to: 'Do',
  rangeHint: 'Koji deo pesme želiš? npr. 0:45 – 1:30. Prazno = od početka.',

  // TikTok.
  tiktokPlaceholder: 'https://www.tiktok.com/@nalog/video/…',
  tiktokLocked: 'Ovaj TikTok je zaključan',
  tiktokLockedNote: 'Privatan je, uklonjen ili nedostupan — njegov zvuk ne može da se preuzme. Nalepi link drugog videa.',
  tiktokLater: '✓ Link je sačuvan',
  tiktokLaterNote: 'Ne možemo odmah da ga pustimo ovde, ali zvuk preuzimamo i sečemo za tebe posle porudžbine.',
  /** Retry only: a pasted link starts loading by itself. */
  loadSound: 'Učitaj zvuk',
  tiktokHint: 'Kopiraj link videa iz TikTok aplikacije (Share → Copy link).',
  tiktokNotLink: 'To ne liči na TikTok link — kopiraj ga iz aplikacije (Share → Copy link).',
  /** Said the moment a link is pasted: the link is the answer, the sound only a bonus. */
  tiktokSaved: '✓ Link je sačuvan — možeš odmah dalje. Zvuk učitavamo u pozadini ako želiš da ga poslušaš ili iseciš.',
  tiktokComing: 'Zvuk stiže za koji trenutak — ništa ne moraš da čekaš.',
  tiktokReady: '✓ Zvuk je spreman — poslušaj ga ili iseci deo.',
  tiktokWhole: 'Ceo zvuk',

  // Files.
  chooseAudio: 'Izaberi zvučni fajl',
  addMoreAudio: 'Dodaj još fajlova',
  removeFile: 'Ukloni fajl',
  fileUnsupported: 'ovaj format zvuka nije podržan. Pošalji MP3, WAV, M4A ili OGG.',
  cut: 'Iseci',

  // Voice.
  startRecording: 'Snimaj',
  stopRecording: 'Završi snimanje',
  recordHint: (min: number) => `Dodirni mikrofon i pričaj (do ${min} min). Dodirni ponovo kad završiš.`,
  recordingNow: 'Snima se — dodirni kvadrat kad završiš.',
  saving: 'Čuvamo snimak…',
  saved: '✓ Snimak je sačuvan. Poslušaj ga, iseci ili snimi ponovo.',
  saveFailed: 'Snimak nije sačuvan — proveri internet.',
  saveBusy: 'Snimak nije sačuvan — server je trenutno zauzet. Sačekaj malo pa pokušaj ponovo.',
  /** A file that does not open as a recording: a new take is the only cure. */
  takeBroken: 'Snimak se nije zapisao kako treba. Snimi ga ponovo.',
  takeTooBig: 'Snimak je prevelik da bi se sačuvao. Snimi kraću poruku.',
  /** A new take was not saved, and the one before it still is the answer. */
  previousKept: 'Prethodni snimak je i dalje sačuvan.',
  retrySave: 'Pokušaj ponovo',
  reRecord: 'Snimi ponovo',
  deleteTake: 'Obriši',
  /** Keeps this voice message and opens a new, empty recorder beside it. */
  voiceAnother: '+ Snimi još jednu poruku',
  micOpening: 'Uključujemo mikrofon…',
  micDenied: 'Nije moguće pristupiti mikrofonu. Dozvoli mikrofon u pregledaču (ikonica u adresnoj traci) pa pokušaj ponovo.',
  micUnsupported: 'Ovaj pregledač ne podržava snimanje — otpremi fajl umesto toga.',
  micSilent:
    'Nismo čuli ništa — ovaj mikrofon šalje tišinu. Izaberi drugi mikrofon ispod i pritisni „Proveri mikrofon“: kad se crtice pomeraju dok pričaš, snimi ponovo.',
  micEmpty:
    'Snimak je prazan — izabrani mikrofon nije poslao zvuk. Izaberi drugi mikrofon ispod i pritisni „Proveri mikrofon“. Na računaru proveri i Windows: Podešavanja → Privatnost → Mikrofon.',
  micQuietLive: 'Ne čujemo te — ovaj mikrofon ne šalje zvuk. Izaberi drugi mikrofon ispod.',
  micPick: 'Mikrofon',
  micCheck: 'Proveri mikrofon',
  micCheckStop: 'Završi proveru',
  micChecking: 'Pričaj — ako se crtice pomeraju, mikrofon radi. Onda pritisni mikrofon da snimiš.',
  tooShort: 'Snimak je prekratak — drži i pričaj bar sekundu.',
  tooLong: (min: number) => `Stigao si do ${min} min — snimanje je završeno.`,

  // AI voice.
  ttsPlaceholder: 'Srećan rođendan ljubavi, volim te najviše ❤️',
  ttsExample: 'Poslušaj kako zvuči AI glas:',
  ttsVoiceLabel: 'Izaberi glas',
  ttsGenerate: 'Napravi glasovnu',
  ttsRegenerate: 'Napravi ponovo',
  ttsWorking: 'AI snima tvoju poruku u dve verzije…',
  ttsPick: 'Poslušaj i izaberi verziju koja ti se više sviđa:',
  ttsTake: (n: number) => `Verzija ${n}`,
  ttsChoose: 'Izaberi ovu',
  ttsChosen: '✓ Izabrana',
  ttsMissing: 'Unesi tekst poruke.',

  // AI song.
  songStyleLabel: 'Kakvu pesmu želiš?',
  songCustomStyle: 'Opiši svoj stil',
  songCustomPlaceholder: 'npr. lagana akustična pesma uz gitaru, romantična…',
  songVoiceLabel: 'Glas pevača',
  songMale: 'Muški',
  songFemale: 'Ženski',
  songLyricsLabel: 'Tekst pesme (ili opiši o čemu treba da bude)',
  songLyricsPlaceholder:
    'Napiši svoj tekst sa [Verse] i [Chorus] oznakama — ili samo opiši: "Pesma za mamu Milicu za 50. rođendan, voli kafu i unuke…" pa klikni AI dugme.',
  songAiLyrics: 'Neka AI napiše tekst',
  songAiWorking: 'AI piše tekst…',
  songGenerate: 'Napravi pesmu',
  songAgain: 'Napravi novu pesmu',
  songPickVersion: 'Poslušaj obe verzije i izaberi svoju:',
  songSelect: 'Ovo je moja pesma',
  songSelected: '✓ Izabrana',
  songCutOptional: 'Iseci pesmu (opciono)',
  songStyleMissing: 'Izaberi ili opiši stil pesme.',
  songLyricsMissing: 'Unesi tekst pesme (bar 20 znakova) ili opis pa klikni AI dugme.',

  kept: 'Sačuvano ranije',
};

/** Style chips: Serbian label, English prompt the model actually receives. */
const SONG_STYLES: Array<{ label: string; prompt: string }> = [
  {
    label: 'Pop',
    prompt:
      'modern Balkan pop, emotional and catchy melody, radio-friendly, warm chords, contemporary production, strong chorus, romantic vibe',
  },
  {
    label: 'Narodna',
    prompt:
      'traditional Balkan folk music, accordion, emotional heartfelt vocals, tavern atmosphere, melodic',
  },
  {
    label: 'Balada',
    prompt: 'slow emotional ballad, piano and strings, heartfelt vocals, romantic, touching',
  },
  {
    label: 'Dens',
    prompt: 'upbeat dance pop, energetic modern beat, catchy hooks, party atmosphere, festive',
  },
  {
    label: 'Rok',
    prompt: 'Balkan rock ballad, electric guitars, powerful chorus, emotional vocals, anthemic',
  },
  {
    label: 'Trubači',
    prompt:
      'Balkan brass band, trumpets, festive and energetic, traditional celebration music, joyful',
  },
  {
    label: 'Folk hit',
    prompt: 'modern turbo folk, strong beat, accordion riffs, catchy chorus, Balkan club energy',
  },
  {
    label: 'Dečija',
    prompt: 'cheerful children song, playful simple melody, sweet and happy, singalong',
  },
];

/**
 * The stages an AI song goes through, as the provider reports them (see
 * apps/api/src/services/song.ts): where the bar stands at each, and what it
 * says. A made-up percent used to climb by itself; these are only what is known.
 */
const SONG_STAGES: Record<'start' | 'lyrics' | 'first', { step: number; at: number; text: string }> = {
  start: { step: 1, at: 15, text: 'Komponujemo melodiju za tvoj tekst…' },
  lyrics: { step: 2, at: 50, text: 'Pevač snima tvoju pesmu…' },
  first: { step: 3, at: 85, text: 'Prva verzija je gotova, završavamo i drugu…' },
};

/** 75 000 ms → "1:15". */
function elapsedClock(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** One line-drawn glyph per source (see icons.ts) — emoji drew differently on every phone. */
const SOURCE_ICONS: Record<AudioKind, string> = {
  song: ICONS.music,
  voice: ICONS.mic,
  tts: ICONS.sparkle,
  youtube: ICONS.play,
  tiktok: ICONS.film,
  upload: ICONS.folder,
};

const SOURCE_DESCS: Record<AudioKind, string> = {
  song: TEXTS.songDesc,
  voice: TEXTS.voiceDesc,
  tts: TEXTS.ttsDesc,
  youtube: TEXTS.youtubeDesc,
  tiktok: TEXTS.tiktokDesc,
  upload: TEXTS.uploadDesc,
};

const SOURCE_LABELS: Record<AudioKind, string> = {
  song: TEXTS.song,
  voice: TEXTS.voice,
  tts: TEXTS.tts,
  youtube: TEXTS.youtube,
  tiktok: TEXTS.tiktok,
  upload: TEXTS.upload,
};

const PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>';
const PAUSE_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor"/></svg>';
const STOP_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="6.5" width="11" height="11" rx="2" fill="currentColor"/></svg>';
const MIC_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>';
const UP_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5-5 5 5"/></svg>';
const DOWN_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 8 5 5 5-5"/></svg>';
const UPLOAD_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13V4M6 7.5l4-4 4 4M4 16.5h12"/></svg>';

/** Where this browser remembers the microphone the shopper picked. */
const MIC_KEY = 'pz-mic';
/** Below this loudest moment (0–1) a take is silence. */
const SILENT_PEAK = 0.012;
/** A stop tapped this soon after the start is the second half of a double tap. */
const DOUBLE_TAP_MS = 600;
/** How long a tap waits on a microphone switching on before a new tap starts over. */
const MIC_PATIENCE_MS = 4000;

/**
 * Whether a recording opens the way the server recognises one (WebM, MP4/M4A,
 * Ogg, WAV, MP3 — see UPLOAD_TYPES in the API's public.ts). A browser that
 * cannot read the bytes gets the benefit of the doubt: the server decides.
 */
export async function recordingHeaderOk(blob: Blob): Promise<boolean> {
  try {
    if (typeof blob.slice !== 'function') return true;
    const part = blob.slice(0, 12);
    if (typeof part.arrayBuffer !== 'function') return true;
    const b = new Uint8Array(await part.arrayBuffer());
    if (b.length < 12) return false;
    return (
      (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) ||
      (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) ||
      (b[0] === 0x4f && b[1] === 0x67 && b[2] === 0x67) ||
      (b[0] === 0x52 && b[8] === 0x57 && b[9] === 0x41) ||
      (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) ||
      (b[0] === 0xff && (b[1]! & 0xe0) === 0xe0)
    );
  } catch {
    return true;
  }
}

/** A live level meter over a microphone stream. */
interface Meter {
  stop: () => void;
  /** The loudest moment so far, 0–1; null when it could not be measured. */
  peak: () => number | null;
  /** How many readings a running audio context took. */
  readings: () => number;
}

/** An audio context made inside a tap, so a phone lets it run; undefined where there is none. */
function newContext(): AudioContext | undefined {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  try {
    const context = Ctor ? new Ctor() : undefined;
    void context?.resume().catch(() => undefined);
    return context;
  } catch {
    return undefined;
  }
}

/**
 * Draws the microphone's level on the bars and keeps the loudest moment. A
 * timer, not animation frames: those stop in a hidden tab, and a meter that
 * stopped reading would call a good take silent. `onQuiet` runs once when
 * two seconds went by and nothing was heard.
 */
function startMeter(
  stream: MediaStream,
  context: AudioContext | undefined,
  bars: HTMLElement[],
  onQuiet?: () => void,
): Meter {
  let peak: number | null = null;
  let readings = 0;
  let timer: number | undefined;
  if (context) {
    try {
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      const history: number[] = [];
      timer = window.setInterval(() => {
        analyser.getFloatTimeDomainData(samples);
        let level = 0;
        for (const value of samples) level = Math.max(level, Math.abs(value));
        if (context.state === 'running') {
          readings++;
          peak = Math.max(peak ?? 0, level);
          if (readings === 30 && (peak ?? 0) < SILENT_PEAK) onQuiet?.();
        }
        history.push(level);
        if (history.length > bars.length) history.shift();
        bars.forEach((bar, i) => {
          const value = history[history.length - bars.length + i] ?? 0;
          bar.style.transform = `scaleY(${Math.max(0.12, Math.min(1, value * 2.4))})`;
        });
      }, 70);
    } catch {
      peak = null;
    }
  }
  return {
    stop: () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
      void context?.close().catch(() => undefined);
      for (const bar of bars) bar.style.transform = 'scaleY(.12)';
    },
    peak: () => peak,
    readings: () => readings,
  };
}

/** The pauses offered before a sound, seconds. */
const GAP_CHOICES = [0, 0.5, 1, 2, 3, 5];

/**
 * Whether a selection is an actual answer. A file or a link always is; a
 * YouTube song given only by its name is too — production (or the worker's
 * search) finds it after the order, so it must satisfy a required field.
 */
export function hasSound(sel: AudioSelection): boolean {
  return Boolean(
    sel.url || sel.storageKey || (sel.kind === 'youtube' && (sel.title?.trim().length ?? 0) >= 2),
  );
}

/**
 * The cutter's effects as the order carries them: only what the shopper
 * actually set. "No fade" and full volume stay unset — an explicit 0 told the
 * server to cut with no fade at all, which drops the 40 ms it puts on every
 * cut point so the cut does not click (packages/audio pipeline), and merely
 * opening a restored sound's editor changed the design.
 */
export function chosenEffects(effects: AudioEffects): Pick<AudioSelection, 'fadeInMs' | 'fadeOutMs' | 'volumePercent'> {
  return {
    fadeInMs: effects.fadeInMs > 0 ? effects.fadeInMs : undefined,
    fadeOutMs: effects.fadeOutMs > 0 ? effects.fadeOutMs : undefined,
    volumePercent: effects.volumePercent !== 100 ? effects.volumePercent : undefined,
  };
}

/**
 * A TikTok post no download can reach: private, removed, or closed to every
 * address tried (the server walks its routes, cobalt and tikwm before it says so).
 */
export function lockedTiktok(error: string | null | undefined): boolean {
  return /blocked from accessing this post|This (post|video) is private|status code 1020[46]|Video not available|post\.(private|unavailable)|content\.(post|video)\.(private|unavailable)/i.test(
    error ?? '',
  );
}

/**
 * Whether a failed fetch is locked at the source. The server says so itself
 * (`code: 'locked'`) and keeps its raw reason private; the wording test is
 * only for a server that sends the reason and no code.
 */
export function prepLocked(row: Pick<AudioPrepStatus, 'status' | 'error' | 'code'>): boolean {
  if (row.status !== 'error') return false;
  if (row.code === 'locked') return true;
  return row.code == null && lockedTiktok(row.error);
}

/** Formats milliseconds as m:ss. */
function fmtMs(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Parses "1:05" or "65" into milliseconds. */
export function parseTime(input: string): number | undefined {
  const text = input.trim();
  if (text === '') return undefined;
  const parts = text.split(':');
  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return undefined;
    return Math.round((minutes * 60 + seconds) * 1000);
  }
  const seconds = Number(text);
  return Number.isFinite(seconds) ? Math.round(seconds * 1000) : undefined;
}

// ── Length and Premium ───────────────────────────────────────────────────────

/** How long the sounds may be together, and what a longer arrangement costs. */
export interface LengthRules {
  /** Included in the price, ms. */
  baseMs: number;
  /** The most the product accepts, ms: the Premium length when there is one. */
  limitMs: number;
  premium: { maxMs: number; price: number; label: string } | null;
}

/**
 * The sound question's length rules. Mirrors the server's parser
 * (services/sound-length.ts), which is what actually bills: a malformed
 * Premium is dropped rather than guessed at.
 */
export function readLengthRules(config: AudioOptionConfig, profile?: AudioProfileDef): LengthRules {
  const profileSeconds = profile?.maxSeconds && profile.maxSeconds > 0 ? profile.maxSeconds : 240;
  const rawBase = Number(config.length?.baseSeconds);
  const baseSeconds =
    Number.isFinite(rawBase) && rawBase >= 10 && rawBase <= 600 ? Math.round(rawBase) : profileSeconds;
  const raw = config.length?.premium;
  const maxSeconds = Number(raw?.maxSeconds);
  const price = Number(raw?.price);
  const premium =
    raw && Number.isFinite(maxSeconds) && maxSeconds > baseSeconds && maxSeconds <= 600 && Number.isFinite(price) && price >= 0
      ? {
          maxMs: Math.round(maxSeconds) * 1000,
          price: Math.round(price * 100) / 100,
          label: raw.label?.trim().slice(0, 80) || `Premium dužina (do ${minutesWord(maxSeconds)} min)`,
        }
      : null;
  return { baseMs: baseSeconds * 1000, limitMs: premium?.maxMs ?? baseSeconds * 1000, premium };
}

/** 480 → "8", 450 → "7,5". */
function minutesWord(seconds: number): string {
  const minutes = Math.round((seconds / 60) * 10) / 10;
  return String(minutes).replace('.', ',');
}

/** The pause before a sound, ms, as the server clamps it. */
function gapOf(sel: AudioSelection): number {
  return Math.min(10_000, Math.max(0, Math.round(sel.gapMs ?? 0)));
}

/** How long one sound will play, ms — or null while nobody can know yet. */
export function clipLengthMs(sel: AudioSelection): number | null {
  const from = sel.fromMs ?? 0;
  if (sel.toMs != null && sel.toMs > from) {
    return sel.durationMs ? Math.max(0, Math.min(sel.durationMs, sel.toMs) - Math.min(sel.durationMs, from)) : sel.toMs - from;
  }
  if (sel.durationMs && sel.durationMs > 0) return Math.max(0, sel.durationMs - from);
  return null;
}

export interface MixTotals {
  /** Everything that can be measured, pauses included, ms. */
  knownMs: number;
  /** Sounds whose length nobody knows yet (a song given only by name). */
  unknown: number;
  premium: boolean;
  over: boolean;
  /** The Premium price when it applies, else 0. */
  fee: number;
}

/**
 * The arrangement against the rules — the same sum the server makes at
 * add-to-cart: every pause, every known length. Premium applies the moment the
 * known total passes the included length.
 */
export function mixTotals(sels: AudioSelection[], rules: LengthRules): MixTotals {
  let knownMs = 0;
  let unknown = 0;
  for (const sel of sels) {
    knownMs += gapOf(sel);
    const length = clipLengthMs(sel);
    if (length === null) unknown++;
    else knownMs += length;
  }
  const premium = Boolean(rules.premium) && knownMs > rules.baseMs;
  return {
    knownMs,
    unknown,
    premium,
    over: knownMs > rules.limitMs,
    fee: premium ? rules.premium!.price : 0,
  };
}

/**
 * What an editor says under its pick while the sounds together run past what
 * the product plays — in place of "možeš odmah dalje", which the order would
 * not honour (the server refuses a known total over the limit). `how` is what
 * the shopper can do about it from here:
 * - `cut`: this sound's cutter is there (and opened for them);
 * - `later`: "Tvoja pesma" before its video is found — its cutter comes with it;
 * - `choose`: the likely versions are listed — one of them brings the cutter;
 * - `words`: no video — the part in words ("Deo pesme", od–do);
 * - `others`: this sound's length is not known yet, so it is not what runs over.
 */
export function overPrompt(opts: {
  maxMs: number;
  /** This sound alone runs past the limit. */
  alone: boolean;
  sound: 'song' | 'tiktok';
  how: 'cut' | 'later' | 'choose' | 'words' | 'others';
}): string {
  const max = fmtMs(opts.maxMs);
  if (opts.how === 'others') return `${TEXTS.overHeadMix(max)} — ${TEXTS.overOthers}`;
  const head = !opts.alone
    ? TEXTS.overHeadMix(max)
    : opts.sound === 'tiktok'
      ? TEXTS.overHeadTiktok(max)
      : TEXTS.overHeadSong(max);
  const what =
    opts.how === 'later'
      ? TEXTS.overLater
      : opts.how === 'choose'
        ? TEXTS.overChoose
        : opts.how === 'words'
          ? TEXTS.overWords
          : opts.sound === 'tiktok'
            ? TEXTS.overCutTiktok
            : TEXTS.overCutSong;
  return `${head} — ${what}`;
}

// ── The control ──────────────────────────────────────────────────────────────

export interface AudioControlOptions {
  option: OptionDef;
  api: Api;
  onChange: (selections: AudioSelection[]) => void;
  /**
   * The Premium price whenever it starts or stops applying (0 = none), so the
   * form's running total can include it. The server bills the same rule.
   */
  onExtraPrice?: (amount: number, label?: string) => void;
  /** The shop's price format ("400 RSD"). */
  formatPrice?: (amount: number) => string;
  /** Sounds restored from an earlier commit or this browser's draft. */
  initial?: AudioSelection[];
  /** What the product's sound module holds; the running total is shown against it. */
  profile?: AudioProfileDef;
  /**
   * The product's one length rule: the first sound question on it that has
   * one (a design's own, else the Sound add-on's) — the rule the server bills
   * by. A product that asks for sound in two places must not call 5:00 "too
   * long" in one of them while the order would be accepted as Premium.
   */
  productLength?: AudioOptionConfig['length'];
  /**
   * The song picked in this product's Spotify picker, if any, and a way to
   * hear when it changes. The YouTube editor then offers it in one tap
   * ("Koristi izabranu pesmu") instead of making the shopper type it again,
   * and with `offerSpotifySong` the step opens on it ("Tvoja pesma").
   * Spotify itself never provides the audio: the same song is found on YouTube.
   */
  pickedSong?: {
    current: () => PickedSong | null;
    subscribe: (listener: () => void) => void;
  };
  /**
   * Hands the form a way to silence the whole step: the popup closed or the
   * shopper moved to another step, and a song nobody can see any more must
   * not keep playing (nor a microphone keep recording).
   */
  registerStop?: (stop: () => void) => void;
  /** Operator imports use the same upload and arrangement as the file picker. */
  registerImport?: (accept: (file: File) => Promise<void>) => void;
}

/** Reads and defaults the option's studio configuration. */
function readConfig(option: OptionDef): Required<Pick<AudioOptionConfig, 'sources' | 'multiple'>> &
  AudioOptionConfig {
  const raw = (option.config ?? {}) as AudioOptionConfig;
  const known: AudioKind[] = ['song', 'voice', 'tts', 'youtube', 'tiktok', 'upload'];
  let sources = Array.isArray(raw.sources)
    ? raw.sources.filter((s): s is AudioKind => known.includes(s))
    : (['youtube', 'upload', 'voice'] as AudioKind[]);
  // Tiles the shop cannot serve are not offered, whatever the config says.
  if (!raw.songAvailable) sources = sources.filter((s) => s !== 'song');
  if (!raw.ttsAvailable) sources = sources.filter((s) => s !== 'tts');
  if (sources.length === 0) sources = ['upload'];
  return { ...raw, sources, multiple: raw.multiple !== false };
}

/** A panel: one source's editor, kept alive while its sound is in the arrangement. */
interface Panel {
  importFile?: (file: File) => Promise<void>;
  host: HTMLElement;
  cleanups: Array<() => void>;
  /** Removes one sound of this panel (an uploaded file) and its row. */
  removeKey?: (key: string) => void;
  /** The YouTube player, for "Preslušaj sve" before the song's own file is here. */
  clip?: () => YoutubePlayer | null;
  /**
   * YouTube: this video is the Spotify song. `prepId`: its fetch, already
   * started by the match asked ahead (a fallback if preparing is refused).
   */
  useVideo?: (video: YoutubeVideo, song: PickedSong, query: string, others?: YoutubeVideo[], prepId?: string) => void;
  /** YouTube: search for the Spotify song; its name alone is already the answer. */
  useQuery?: (query: string, song: PickedSong) => void;
  /** YouTube: "Tvoja pesma" → Da — the whole song by its name, the answer at once. */
  useSong?: (song: PickedSong) => void;
  /** YouTube: the match for that song arrived — the video, a choice, or the name stays. */
  upgradeSong?: (song: PickedSong, match: YoutubeMatch) => void;
  /** Stops a recording (it is kept) or a microphone check: the editor is being left. */
  halt?: () => void;
  /** A take is being recorded or saved: an editor closed now must not be thrown away. */
  busy?: () => boolean;
}

/** The kind a selection key belongs to: "upload:2" → upload. */
const kindOf = (key: string): AudioKind => key.split(':')[0] as AudioKind;

/** The editor a sound lives in: its own (`youtube:2`), or the one files editor. */
const panelIdOf = (key: string): string => (kindOf(key) === 'upload' ? 'upload' : key);

/** Sounds one order may carry (the server keeps at most ten). */
export const MAX_SOUNDS = 10;

export function createAudioControl(options: AudioControlOptions): HTMLElement {
  const { api, option, onChange } = options;
  const config = readConfig(option);
  const rules = readLengthRules(options.productLength ? { ...config, length: options.productLength } : config, options.profile);
  const price = (amount: number): string => options.formatPrice?.(amount) ?? `${amount} RSD`;
  const single = config.sources.length === 1;

  const root = document.createElement('div');
  root.className = 'pz-audio';

  // ── State ──────────────────────────────────────────────────────────────────
  const selections = new Map<string, AudioSelection>();
  /** Play order, as the shopper arranged it. A new sound joins at the end. */
  const order: string[] = [];
  /**
   * One editor per sound: `youtube`, `youtube:2`, `voice:3`… — a second song
   * from YouTube is a second editor with its own player and its own od–do.
   * Files are the exception: one editor holds every uploaded file
   * (`upload:0`, `upload:1`…), see `panelIdOf`.
   */
  const panels = new Map<string, Panel>();
  /** The editor on screen, by its id. */
  let openKind: string | null = null;
  let lastFee = 0;

  /** Every player on the step, so starting one stops the others. */
  const players = new Set<() => void>();
  const pauseAll = (except?: () => void): void => {
    for (const pause of players) if (pause !== except) pause();
  };
  /** What else "stop everything" ends: a recording, a microphone check. */
  const stops = new Set<() => void>();
  options.registerStop?.(() => {
    stopPlayAll();
    pauseAll();
    for (const stop of stops) stop();
  });

  /**
   * A player the step does not build itself (a cutter, YouTube's): `pause`
   * joins the step's players, and `onPlay` — its own play button — stops the
   * others, "Preslušaj sve" included. `pause` reads the current player, so one
   * registration serves an editor whose player is replaced.
   */
  const joinPlayers = (key: string, current: () => { pause: () => void } | null): { onPlay: () => void } => {
    const pause = (): void => current()?.pause();
    players.add(pause);
    onCleanup(key, () => players.delete(pause));
    return {
      onPlay: () => {
        stopPlayAll();
        pauseAll(pause);
      },
    };
  };

  const orderedKeys = (): string[] => {
    const kept = order.filter((key) => selections.has(key));
    const fresh = [...selections.keys()].filter((key) => !kept.includes(key));
    order.length = 0;
    order.push(...kept, ...fresh);
    return order;
  };
  const soundKeys = (): string[] => orderedKeys().filter((key) => hasSound(selections.get(key)!));
  /** Whether an editor holds a sound: its own, or (the files editor) any file. */
  const panelHasSound = (id: string): boolean =>
    [...selections.entries()].some(([key, sel]) => panelIdOf(key) === id && hasSound(sel));

  /** The sounds in play order; the first never waits (a pause before nothing is silence). */
  const arranged = (): AudioSelection[] =>
    soundKeys().map((key, index) => {
      const sel = selections.get(key)!;
      return index === 0 && sel.gapMs ? { ...sel, gapMs: 0 } : sel;
    });

  /** Restored sounds shown in their editors, until something replaces them. */
  const keptBoxes = new Map<string, { box: HTMLElement; storageKey?: string }>();

  /**
   * Editors whose words follow the whole arrangement ("možeš odmah dalje" only
   * while everything together fits): told after every change to any sound.
   */
  const mixWatchers = new Set<() => void>();

  /**
   * The sounds together run past what the product plays, as it concerns one
   * of them: null while they fit (or it holds no sound yet). `known`: its own
   * length counts in the total; `alone`: it runs past the limit by itself.
   */
  const overFor = (key: string): { alone: boolean; known: boolean } | null => {
    const sel = selections.get(key);
    if (!sel || !hasSound(sel) || !mixTotals(arranged(), rules).over) return null;
    const length = clipLengthMs(sel);
    return { alone: length !== null && length > rules.limitMs, known: length !== null };
  };

  const emit = (): void => {
    for (const [key, kept] of keptBoxes) {
      if (selections.get(key)?.storageKey !== kept.storageKey) {
        kept.box.remove();
        keptBoxes.delete(key);
      }
    }
    const list = arranged();
    const totals = mixTotals(list, rules);
    // The price first: the answer below is what redraws the form's total.
    if (totals.fee !== lastFee) {
      lastFee = totals.fee;
      options.onExtraPrice?.(totals.fee, rules.premium?.label);
    }
    onChange(list.map((sel) => ({ ...sel, previewUrl: undefined })));
    renderMix(totals);
    renderTiles();
    renderEditorFoot();
    renderOffer();
    for (const watch of mixWatchers) watch();
  };

  const patch = (key: string, partial: Partial<AudioSelection>): void => {
    const kind = kindOf(key);
    const current = selections.get(key) ?? { kind, slot: key };
    const next = { ...current, ...partial, kind, slot: key };
    selections.set(key, next);
    // One sound only: a new one replaces what the other tiles had.
    if (!config.multiple && hasSound(next)) {
      for (const other of [...selections.keys()]) {
        if (kindOf(other) !== kind) {
          selections.delete(other);
          destroyPanel(panelIdOf(other));
        }
      }
    }
    emit();
  };

  const clearKey = (key: string): void => {
    selections.delete(key);
    emit();
  };

  /** Forgets whatever an editor left behind (an empty one, closed). */
  const clearPanel = (id: string): void => {
    for (const key of [...selections.keys()]) if (panelIdOf(key) === id) selections.delete(key);
    emit();
  };

  /** Runs `fn` when the editor that `key` (a sound or an editor id) belongs to is thrown away. */
  const onCleanup = (key: string, fn: () => void): void => {
    panels.get(panelIdOf(key))?.cleanups.push(fn);
  };

  /** Where stored files are served from (recordings, AI voices, prepared songs). */
  const fileUrl = (storageKey: string): string => `${api.baseUrl.replace(/\/+$/, '')}/files/${storageKey}`;

  // ── Layout ─────────────────────────────────────────────────────────────────
  const mixBox = document.createElement('section');
  mixBox.className = 'pz-mix';
  mixBox.hidden = true;

  const addBox = document.createElement('div');
  addBox.className = 'pz-snd-add';
  const addLabel = document.createElement('p');
  addLabel.className = 'pz-sub-label';
  const tiles = document.createElement('div');
  tiles.className = 'pz-tiles';
  addBox.append(addLabel, tiles);

  const editor = document.createElement('div');
  editor.className = 'pz-snd-editor';
  editor.hidden = true;
  const editorHead = document.createElement('div');
  editorHead.className = 'pz-snd-editor-head';
  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.className = 'pz-snd-back';
  backBtn.innerHTML = `${ICONS.chevronLeft}<span>${TEXTS.back}</span>`;
  const editorIcon = document.createElement('span');
  editorIcon.className = 'pz-src-icon';
  const editorTitle = document.createElement('strong');
  editorHead.append(backBtn, editorIcon, editorTitle);
  const editorBodies = document.createElement('div');
  editorBodies.className = 'pz-snd-editor-bodies';
  const editorFoot = document.createElement('div');
  editorFoot.className = 'pz-snd-editor-foot';
  const doneBtn = document.createElement('button');
  doneBtn.type = 'button';
  doneBtn.className = 'pz-btn pz-snd-done';
  doneBtn.innerHTML = `${ICONS.check}<span>${TEXTS.done}</span>`;
  const doneHint = document.createElement('span');
  doneHint.className = 'pz-hint';
  doneHint.textContent = TEXTS.doneHint;
  editorFoot.append(doneBtn, doneHint);
  const addAnother = document.createElement('button');
  addAnother.type = 'button';
  addAnother.className = 'pz-btn-ghost small pz-snd-add-another';
  addAnother.hidden = true;
  addAnother.addEventListener('click', () => {
    if (openKind) addSound(kindOf(openKind));
  });
  editor.append(editorHead, editorBodies, editorFoot, addAnother);

  // "Tvoj zvuk" can open on the song already picked on Spotify: the card is
  // the step's first screen until the shopper answers it.
  const offerBox = document.createElement('section');
  offerBox.className = 'pz-snd-offer';
  offerBox.hidden = true;
  const againBtn = document.createElement('button');
  againBtn.type = 'button';
  againBtn.className = 'pz-btn-ghost small pz-snd-offer-again';
  againBtn.hidden = true;
  addBox.appendChild(againBtn);

  root.append(offerBox, mixBox, addBox, editor);

  const tileEls = new Map<AudioKind, { tile: HTMLButtonElement; badge: HTMLElement }>();
  for (const kind of config.sources) {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'pz-tile';
    const icon = document.createElement('span');
    icon.className = 'pz-src-icon';
    icon.innerHTML = SOURCE_ICONS[kind];
    const copy = document.createElement('span');
    copy.className = 'pz-tile-copy';
    const label = document.createElement('strong');
    label.textContent = labelOf(kind);
    const desc = document.createElement('span');
    desc.textContent = SOURCE_DESCS[kind];
    copy.append(label, desc);
    const badge = document.createElement('span');
    badge.className = 'pz-tile-badge';
    badge.hidden = true;
    tile.append(icon, copy, badge);
    tile.addEventListener('click', () => addSound(kind));
    tiles.appendChild(tile);
    tileEls.set(kind, { tile, badge });
  }

  // ── "Tvoja pesma": the Spotify song, offered first ────────────────────────
  //
  // Only where the shop ticked it, the product has a Spotify question and the
  // YouTube card exists: Spotify gives no audio, so the song is found there.
  const offerOn = config.offerSpotifySong === true && config.sources.includes('youtube') && Boolean(options.pickedSong);
  /** Songs the shopper already answered ("Ne", or "Da" and on to the editor), by track: another song asks again. */
  const declined = new Set<string>();
  /** What the card last drew, so a redraw happens only when something changed. */
  let offerDrawn = '';
  let offerRendering = false;

  const pickedNow = (): PickedSong | null => options.pickedSong?.current() ?? null;

  /**
   * The Spotify song is matched on YouTube while the shopper is still on the
   * Spotify step (this control is built with the form and hears the pick), and
   * the server starts fetching a sure match right then — so "Da" is instant,
   * and the file is often here before the sound step opens. Only on products
   * that offer the song; a song already among the sounds is not asked about.
   */
  const matchAhead = offerOn
    ? createMatchPrefetch((song) =>
        api.matchYoutube({ title: song.title, artists: song.artists, durationMs: song.durationMs }, { prefetch: true }),
      )
    : null;
  const askAhead = (): void => {
    if (!matchAhead) return;
    const song = pickedNow();
    matchAhead.schedule(song && !songInMix(song, arranged()) ? song : null);
  };
  /** Whether a sound is the one taken from this Spotify song ("Tvoja pesma"). */
  const fromSong = (sel: AudioSelection, song: PickedSong): boolean =>
    Boolean(sel.spotifyUri ? sel.spotifyUri === song.uri : false);

  function renderOffer(): void {
    if (!offerOn || offerRendering) return;
    offerRendering = true;
    try {
      const song = pickedNow();
      const sels = arranged();
      // A song already among the sounds was answered — in this visit ("Da"),
      // or before: an "Izmeni" from the cart or a draft brings it back with
      // them. Changing or removing that sound later must not bring the card
      // back over the editor, asking about the very song being replaced.
      if (song && songInMix(song, sels)) declined.add(songKey(song));
      const show = offerVisible({ enabled: true, song, sels, declined });
      offerBox.hidden = !show;
      root.classList.toggle('is-offering', show);
      // The card is the first screen: the tiles (or, with one source, its
      // editor) come after the answer.
      if (single) editor.hidden = show || !openKind;
      else addBox.hidden = show;
      againBtn.hidden = !song || show || !declined.has(songKey(song)) || songInMix(song, sels);
      if (song && !againBtn.hidden) againBtn.textContent = OFFER_TEXTS.again(song.title);
      if (!show || !song) {
        offerDrawn = '';
        return;
      }
      // A song picked while another editor was open: back to the step's start.
      if (openKind && !single) closeEditor(true);
      const state = [songKey(song), lastFee, sels.length].join('|');
      if (state !== offerDrawn) {
        offerDrawn = state;
        drawOffer(song);
      }
    } finally {
      offerRendering = false;
    }
  }

  function drawOffer(song: PickedSong): void {
    offerBox.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'pz-snd-offer-song';
    if (song.cover) {
      const cover = document.createElement('img');
      cover.className = 'pz-snd-offer-cover';
      cover.src = song.cover;
      cover.alt = '';
      head.appendChild(cover);
    }
    const words = document.createElement('div');
    words.className = 'pz-snd-offer-words';
    const title = document.createElement('strong');
    title.textContent = song.title;
    const by = document.createElement('span');
    by.textContent = [song.artists, song.durationMs ? offerTime(song.durationMs) : ''].filter(Boolean).join(' · ');
    words.append(title, by);
    head.appendChild(words);
    offerBox.appendChild(head);

    const question = document.createElement('p');
    question.className = 'pz-snd-offer-question';
    question.textContent = OFFER_TEXTS.question;
    offerBox.appendChild(question);
    // What the whole song does to the length, before anything loads: Spotify
    // already said how long it is.
    const withSong = mixTotals([...arranged(), { kind: 'youtube', durationMs: song.durationMs }], rules);
    const noteText = offerLengthNote(song, withSong, rules, price);
    if (noteText) {
      const note = document.createElement('p');
      note.className = 'pz-hint pz-snd-offer-note';
      note.textContent = noteText;
      offerBox.appendChild(note);
    }
    const actions = document.createElement('div');
    actions.className = 'pz-snd-offer-actions';
    const yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'pz-btn pz-snd-offer-yes';
    yes.textContent = OFFER_TEXTS.yes;
    yes.addEventListener('click', () => acceptSong(song));
    const no = document.createElement('button');
    no.type = 'button';
    no.className = 'pz-btn-ghost pz-snd-offer-no';
    no.textContent = OFFER_TEXTS.no;
    no.addEventListener('click', () => {
      declined.add(songKey(song));
      renderOffer();
      if (single) openEditor(config.sources[0]!);
    });
    actions.append(yes, no);
    offerBox.appendChild(actions);
  }

  /**
   * "Da" answers at once: the whole song, by Spotify's name, is the sound the
   * moment it is tapped — nothing is waited for. The match asked ahead (or
   * asked now) then turns that same sound into its video, offers the likely
   * versions, or leaves the name as it is (the worker finds it after the order).
   */
  function acceptSong(song: PickedSong): void {
    // Answered: editing the search from here on is the shopper's business,
    // and the card must not come back over the editor they are working in.
    declined.add(songKey(song));
    const id = addSound('youtube');
    const panel = panels.get(id);
    panel?.useSong?.(song);
    const found = matchAhead
      ? matchAhead.get(song)
      : api.matchYoutube({ title: song.title, artists: song.artists, durationMs: song.durationMs });
    void found.then((result) => {
      // The editor may be gone by now (the sound removed, another song picked);
      // the editor itself checks the shopper has not moved on inside it.
      if (panel && panels.get(id) === panel) panel.upgradeSong?.(song, result);
    });
  }

  againBtn.addEventListener('click', () => {
    const song = pickedNow();
    if (song) declined.delete(songKey(song));
    renderOffer();
  });
  // A new Spotify song starts the song over: the sound taken from the old one
  // goes (only that one — voice messages and other songs stay), and the card
  // asks about the new song. Removing the Spotify song takes its sound too.
  let lastSong = pickedNow();
  options.pickedSong?.subscribe(() => {
    const now = pickedNow();
    const was = lastSong;
    lastSong = now;
    if (!was || (now && songKey(now) === songKey(was))) {
      renderOffer();
      askAhead();
      return;
    }
    const gone = [...selections.entries()]
      .filter(([, sel]) => sel.kind === 'youtube' && fromSong(sel, was))
      .map(([key]) => key);
    for (const key of gone) removeSound(key);
    if (now) declined.delete(songKey(now));
    renderOffer();
    askAhead();
  });

  backBtn.addEventListener('click', () => closeEditor());
  doneBtn.addEventListener('click', () => {
    closeEditor();
    mixBox.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  });

  function labelOf(kind: AudioKind): string {
    return config.labels?.[kind]?.trim() || SOURCE_LABELS[kind];
  }

  function renderTiles(): void {
    const any = soundKeys().length > 0;
    const full = config.multiple && soundKeys().length >= MAX_SOUNDS;
    addLabel.textContent = full ? TEXTS.full(MAX_SOUNDS) : !any ? TEXTS.addFirst : config.multiple ? TEXTS.addMore : TEXTS.replace;
    for (const [kind, els] of tileEls) {
      const count = soundKeys().filter((key) => kindOf(key) === kind).length;
      els.badge.hidden = count === 0;
      els.badge.innerHTML = `${ICONS.check}<span>${count > 1 ? TEXTS.addedCount(count) : TEXTS.added}</span>`;
      els.tile.classList.toggle('is-added', count > 0);
      els.tile.disabled = full;
    }
    renderAddAnother();
  }

  /**
   * A tile adds a sound of its kind: the first opens that kind's editor, the
   * next one — another song, a second voice message — a new editor beside it.
   * Files are added inside their one editor, and a one-sound question keeps
   * one editor per kind, as before.
   */
  function addSound(kind: AudioKind): string {
    if (kind === 'upload' || !config.multiple) {
      openEditor(kind);
      return kind;
    }
    const taken = new Set([...selections.keys(), ...panels.keys()]);
    // The kind's own name while it is free (or its editor is still empty).
    if (!taken.has(kind) || !panelHasSound(kind)) {
      openEditor(kind);
      return kind;
    }
    if (soundKeys().length >= MAX_SOUNDS) return kind;
    let n = 2;
    while (taken.has(`${kind}:${n}`)) n++;
    const id = `${kind}:${n}`;
    openEditor(id);
    return id;
  }

  /** With one source there are no tiles: its editor offers "another one" itself. */
  function renderAddAnother(): void {
    const kind = openKind ? kindOf(openKind) : null;
    // A voice message offers the next one everywhere: "keep this one and
    // record another" is what people look for right under their recording.
    addAnother.hidden =
      !(single || kind === 'voice') ||
      !config.multiple ||
      !kind ||
      kind === 'upload' ||
      !panelHasSound(openKind!) ||
      soundKeys().length >= MAX_SOUNDS;
    if (!addAnother.hidden) addAnother.textContent = kind === 'voice' ? TEXTS.voiceAnother : TEXTS.addAnother(labelOf(kind!));
  }

  function renderEditorFoot(): void {
    if (!openKind) return;
    const has = panelHasSound(openKind);
    doneBtn.disabled = !has;
    doneHint.hidden = has;
  }

  /** Builds an editor the first time it is opened; `id` is its sound's key (see `panels`). */
  function ensurePanel(id: string): Panel {
    let panel = panels.get(id);
    if (panel) return panel;
    const host = document.createElement('div');
    host.className = 'pz-snd-editor-body';
    host.hidden = true;
    editorBodies.appendChild(host);
    panel = { host, cleanups: [] };
    panels.set(id, panel);
    builders[kindOf(id)](host, panel, id);
    return panel;
  }

  /** Throws an editor away — its sound was removed, or it was left empty. */
  function destroyPanel(id: string): void {
    const panel = panels.get(id);
    if (!panel) return;
    for (const cleanup of panel.cleanups) cleanup();
    panel.host.remove();
    panels.delete(id);
    if (openKind === id) {
      openKind = null;
      editor.hidden = true;
      root.classList.remove('is-editing');
    }
  }

  function openEditor(id: string): void {
    if (openKind && openKind !== id) closeEditor(true);
    stopPlayAll();
    const panel = ensurePanel(id);
    for (const other of panels.values()) other.host.hidden = other !== panel;
    openKind = id;
    const kind = kindOf(id);
    editorIcon.innerHTML = SOURCE_ICONS[kind];
    editorTitle.textContent = labelOf(kind);
    editor.hidden = false;
    backBtn.hidden = single;
    editorFoot.hidden = single;
    root.classList.add('is-editing');
    renderEditorFoot();
    renderAddAnother();
    if (!single) editor.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }

  /** Back to the arrangement. An editor left without a sound starts fresh next time. */
  function closeEditor(quiet = false): void {
    const id = openKind;
    if (!id || single) return;
    pauseAll();
    const panel = panels.get(id);
    panel?.clip?.()?.pause();
    // A recording ends with the editor, and is kept.
    panel?.halt?.();
    openKind = null;
    editor.hidden = true;
    root.classList.remove('is-editing');
    // An editor still recording or saving its first take stays (hidden) until
    // that take is saved — or comes to nothing and throws it away itself.
    if (!panelHasSound(id) && !panel?.busy?.()) {
      destroyPanel(id);
      clearPanel(id);
    } else if (!quiet) {
      emit();
    }
  }

  /** Takes one sound out of the arrangement. */
  function removeSound(key: string): void {
    stopPlayAll();
    const id = panelIdOf(key);
    const panel = panels.get(id);
    if (panel?.removeKey) {
      panel.removeKey(key);
      return;
    }
    selections.delete(key);
    const reopen = single && openKind === id;
    destroyPanel(id);
    // With one source the editor IS the step: a removed song leaves an
    // empty editor of that kind, or the one that remains.
    if (reopen) openEditor(soundKeys()[0] ? panelIdOf(soundKeys()[0]!) : config.sources[0]!);
    emit();
  }

  function moveKey(key: string, delta: number): void {
    const keys = soundKeys();
    const from = keys.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= keys.length) return;
    const all = orderedKeys();
    const a = all.indexOf(keys[from]!);
    const b = all.indexOf(keys[to]!);
    [all[a], all[b]] = [all[b]!, all[a]!];
    emit();
  }

  // ── "Tvoj zvuk": the arrangement ──────────────────────────────────────────

  function soundLabel(sel: AudioSelection): string {
    return sel.title?.trim() || sel.url || SOURCE_LABELS[sel.kind] || 'Zvuk';
  }

  function renderMix(totals: MixTotals): void {
    const keys = soundKeys();
    const showMix = keys.length > 0 && (!single || keys.length > 1 || Boolean(rules.premium));
    mixBox.hidden = !showMix;
    mixBox.innerHTML = '';
    if (!showMix) {
      stopPlayAll();
      return;
    }
    mixBox.classList.toggle('is-over', totals.over);
    mixBox.classList.toggle('is-premium', totals.premium && !totals.over);

    const head = document.createElement('div');
    head.className = 'pz-mix-head';
    const title = document.createElement('strong');
    title.textContent = TEXTS.mixTitle;
    const sum = document.createElement('span');
    sum.className = 'pz-mix-sum';
    const scaleMs = totals.premium || totals.over ? rules.limitMs : rules.baseMs;
    sum.textContent = `${TEXTS.countSounds(keys.length)} · ${totals.unknown ? '≈ ' : ''}${fmtMs(totals.knownMs)} / ${fmtMs(scaleMs)}`;
    head.append(title, sum);
    mixBox.appendChild(head);

    // The meter: the included length, then the Premium stretch past it.
    const meter = document.createElement('div');
    meter.className = 'pz-mix-meter';
    const bar = document.createElement('div');
    bar.className = 'pz-mix-bar';
    const full = rules.limitMs;
    const fill = document.createElement('div');
    fill.className = 'pz-mix-fill';
    fill.style.width = `${Math.min(100, (totals.knownMs / full) * 100)}%`;
    bar.appendChild(fill);
    if (rules.premium) {
      const zone = document.createElement('div');
      zone.className = 'pz-mix-zone';
      zone.style.left = `${(rules.baseMs / full) * 100}%`;
      const mark = document.createElement('div');
      mark.className = 'pz-mix-mark';
      mark.style.left = `${(rules.baseMs / full) * 100}%`;
      bar.append(zone, mark);
    }
    const scale = document.createElement('div');
    scale.className = 'pz-mix-scale';
    const left = document.createElement('span');
    left.textContent = TEXTS.included(fmtMs(rules.baseMs));
    scale.appendChild(left);
    if (rules.premium) {
      const right = document.createElement('span');
      right.className = 'pz-mix-scale-premium';
      right.textContent = TEXTS.premiumOffer(fmtMs(rules.premium.maxMs), price(rules.premium.price));
      scale.appendChild(right);
    }
    meter.append(bar, scale);
    mixBox.appendChild(meter);

    const note = document.createElement('p');
    note.className = 'pz-mix-note';
    if (totals.over) {
      note.textContent = TEXTS.overLimit(fmtMs(rules.limitMs));
      note.classList.add('is-over');
    } else if (totals.premium && rules.premium) {
      note.textContent = TEXTS.premiumOn(price(rules.premium.price), fmtMs(rules.premium.maxMs));
      note.classList.add('is-premium');
    } else if (rules.premium && totals.knownMs > rules.baseMs - 45_000) {
      note.textContent = TEXTS.premiumSoon(
        fmtMs(rules.baseMs - totals.knownMs),
        fmtMs(rules.baseMs),
        price(rules.premium.price),
        fmtMs(rules.premium.maxMs),
      );
    } else if (totals.unknown) {
      // Said for what it is: a song given by name, or a TikTok still loading.
      const unknownKinds = new Set(keys.filter((key) => clipLengthMs(selections.get(key)!) === null).map((key) => kindOf(key)));
      note.textContent =
        unknownKinds.size === 1 && unknownKinds.has('tiktok')
          ? TEXTS.unknownTiktokNote
          : unknownKinds.has('tiktok')
            ? TEXTS.unknownMixedNote
            : TEXTS.unknownNote;
    }
    note.hidden = !note.textContent;
    mixBox.appendChild(note);

    const list = document.createElement('ol');
    list.className = 'pz-mix-list';
    keys.forEach((key, index) => {
      const sel = selections.get(key)!;
      const length = clipLengthMs(sel);
      const row = document.createElement('li');
      row.className = 'pz-mix-row';
      row.classList.toggle('is-playing', playingKey === key);

      const move = document.createElement('div');
      move.className = 'pz-mix-move';
      move.hidden = keys.length < 2;
      const up = iconButton(UP_ICON, `${TEXTS.moveUp}: ${soundLabel(sel)}`, () => moveKey(key, -1));
      up.disabled = index === 0;
      const down = iconButton(DOWN_ICON, `${TEXTS.moveDown}: ${soundLabel(sel)}`, () => moveKey(key, 1));
      down.disabled = index === keys.length - 1;
      move.append(up, down);

      const icon = document.createElement('span');
      icon.className = 'pz-mix-kind';
      icon.innerHTML = SOURCE_ICONS[sel.kind];

      const text = document.createElement('div');
      text.className = 'pz-mix-text';
      const name = document.createElement('strong');
      name.textContent = `${index + 1}. ${soundLabel(sel)}`;
      const meta = document.createElement('span');
      meta.className = 'pz-mix-meta';
      const len = document.createElement('span');
      len.textContent = length === null ? TEXTS.lengthUnknown : fmtMs(length);
      meta.append(icon, len);
      // The pause is asked for in seconds because that is how people describe
      // it ("pola sekunde tišine"); it is stored and sent in milliseconds.
      if (index > 0) {
        const gap = document.createElement('label');
        gap.className = 'pz-mix-gap';
        const caption = document.createElement('span');
        caption.textContent = TEXTS.pauseBefore;
        const select = document.createElement('select');
        select.className = 'pz-mix-gap-select';
        const current = gapOf(sel) / 1000;
        const choices = GAP_CHOICES.includes(current) ? GAP_CHOICES : [...GAP_CHOICES, current].sort((a, b) => a - b);
        for (const seconds of choices) {
          const item = document.createElement('option');
          item.value = String(seconds);
          item.textContent = `${String(seconds).replace('.', ',')} s`;
          item.selected = seconds === current;
          select.appendChild(item);
        }
        select.addEventListener('change', () => patch(key, { gapMs: Math.round(Number(select.value) * 1000) }));
        gap.append(caption, select);
        meta.appendChild(gap);
      }
      text.append(name, meta);

      const edit = iconButton(ICONS.pencil, `${TEXTS.edit}: ${soundLabel(sel)}`, () => openEditor(panelIdOf(key)));
      edit.classList.add('pz-mix-edit');
      const remove = iconButton(ICONS.close, `${TEXTS.remove}: ${soundLabel(sel)}`, () => removeSound(key));
      remove.classList.add('pz-mix-remove');

      row.append(move, text, edit, remove);
      list.appendChild(row);
    });
    mixBox.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'pz-mix-actions';
    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'pz-btn pz-mix-play';
    play.innerHTML = playRun.active
      ? `${STOP_ICON}<span>${TEXTS.playStop}</span>`
      : `${PLAY_ICON}<span>${keys.length > 1 ? TEXTS.playAll : TEXTS.playOne}${totals.knownMs ? ` (${fmtMs(totals.knownMs)})` : ''}</span>`;
    play.addEventListener('click', () => {
      if (playRun.active) stopPlayAll();
      else startPlayAll();
    });
    actions.appendChild(play);
    mixBox.appendChild(actions);
    if (playNote) {
      const pn = document.createElement('p');
      pn.className = 'pz-mix-note';
      pn.textContent = playNote;
      mixBox.appendChild(pn);
    }
  }

  function iconButton(svg: string, label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-icon-btn';
    button.innerHTML = svg;
    button.title = label;
    button.setAttribute('aria-label', label);
    button.addEventListener('click', onClick);
    return button;
  }

  // ── "Preslušaj sve" ────────────────────────────────────────────────────────
  //
  // Plays the arrangement exactly as the finished file will: each sound's cut,
  // the pauses between them, in order. One audio element is reused for every
  // file — on a phone, the difference between playback the browser allows after
  // one tap and a second sound silently blocked as unrequested autoplay — and a
  // YouTube song plays in its own player, which a phone may want tapped.

  const playRun = { id: 0, active: false };
  let playingKey: string | null = null;
  let playNote = '';
  let runner: HTMLAudioElement | null = null;
  let stopCurrent: (() => void) | null = null;

  function startPlayAll(): void {
    pauseAll();
    const run = ++playRun.id;
    playRun.active = true;
    playNote = '';
    // Unlocked inside the tap, so the later sounds may start on their own.
    runner = runner ?? new Audio();
    runner.src = SILENCE;
    void runner.play().catch(() => undefined);
    const keys = soundKeys();
    const skipped: string[] = [];
    void (async () => {
      for (const [index, key] of keys.entries()) {
        if (run !== playRun.id) return;
        const sel = selections.get(key);
        if (!sel) continue;
        const gap = index === 0 ? 0 : gapOf(sel);
        if (gap > 0) {
          await sleep(gap);
          if (run !== playRun.id) return;
        }
        playingKey = key;
        emit();
        const played = await playOne(key, sel, run);
        if (!played && run === playRun.id) skipped.push(soundLabel(sel));
      }
      if (run !== playRun.id) return;
      playRun.active = false;
      playingKey = null;
      playNote = skipped.length ? skipped.map((title) => TEXTS.playSkipped(title)).join(' ') : '';
      emit();
    })();
    emit();
  }

  function stopPlayAll(): void {
    if (!playRun.active && !playingKey) return;
    playRun.id++;
    playRun.active = false;
    playingKey = null;
    stopCurrent?.();
    stopCurrent = null;
    runner?.pause();
    emit();
  }

  /** Plays one sound of the arrangement; false when it could not be heard. */
  function playOne(key: string, sel: AudioSelection, run: number): Promise<boolean> {
    const src = sel.previewUrl ?? (sel.storageKey ? fileUrl(sel.storageKey) : undefined);
    if (src && runner) {
      const player = runner;
      return new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (ok: boolean): void => {
          if (settled) return;
          settled = true;
          player.removeEventListener('timeupdate', onTime);
          player.removeEventListener('ended', onEnded);
          player.removeEventListener('error', onError);
          player.pause();
          stopCurrent = null;
          resolve(ok);
        };
        const onTime = (): void => {
          // The cut is honoured: hearing the whole song when they chose the
          // chorus would make the preview a lie.
          if (sel.toMs && player.currentTime * 1000 >= sel.toMs) finish(true);
        };
        const onEnded = (): void => finish(true);
        const onError = (): void => finish(false);
        player.addEventListener('timeupdate', onTime);
        player.addEventListener('ended', onEnded);
        player.addEventListener('error', onError);
        stopCurrent = () => finish(false);
        player.src = src;
        const seek = (): void => {
          if (sel.fromMs) player.currentTime = sel.fromMs / 1000;
        };
        if (player.readyState >= 1) seek();
        else player.addEventListener('loadedmetadata', seek, { once: true });
        void player.play().catch(() => finish(false));
      });
    }
    if (sel.kind === 'youtube' && sel.url && parseYoutubeId(sel.url)) {
      const panelId = panelIdOf(sel.slot ?? 'youtube');
      const panel = ensurePanel(panelId);
      const clip = panel.clip?.();
      if (!clip) return Promise.resolve(false);
      stopCurrent = () => clip.stopSegment();
      return clip.playSegment(sel.fromMs ?? 0, sel.toMs, () => {
        if (run !== playRun.id) return;
        // A phone that wants the video itself tapped: show it, and say so.
        playNote = TEXTS.playTap;
        openEditor(panelId);
        playRun.active = true;
        emit();
      });
    }
    return Promise.resolve(false);
  }

  // ── Players ────────────────────────────────────────────────────────────────

  /**
   * A small player of our own: a round play button, the time and a seek bar.
   * The browser's native one drew differently everywhere, at 40 px was hard to
   * hit on a phone, and a fresh recording often showed "0:00 / 0:00" because
   * the file carries no length in its header — this one knows the length the
   * recorder counted. A second address (the stored copy) is tried when the
   * first cannot play.
   */
  function makePlayer(
    src: string,
    opts: { knownMs?: number; fallback?: string; onDuration?: (ms: number) => void } = {},
  ): { element: HTMLElement; pause: () => void; destroy: () => void } {
    const wrap = document.createElement('div');
    wrap.className = 'pz-player';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-player-btn';
    const mid = document.createElement('div');
    mid.className = 'pz-player-mid';
    const seek = document.createElement('input');
    seek.type = 'range';
    seek.className = 'pz-player-seek';
    seek.min = '0';
    seek.max = '1000';
    seek.step = '1';
    seek.value = '0';
    seek.setAttribute('aria-label', TEXTS.seek);
    const time = document.createElement('span');
    time.className = 'pz-player-time';
    mid.append(seek, time);
    wrap.append(button, mid);

    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = src;
    let durationMs = opts.knownMs && opts.knownMs > 0 ? opts.knownMs : 0;
    let triedFallback = false;
    let dragging = false;

    const show = (): void => {
      const at = audio.currentTime * 1000;
      time.textContent = `${fmtMs(at)} / ${durationMs ? fmtMs(durationMs) : '–:––'}`;
      if (!dragging && durationMs) seek.value = String(Math.round(Math.min(1, at / durationMs) * 1000));
    };
    const icon = (): void => {
      const on = !audio.paused;
      button.innerHTML = on ? PAUSE_ICON : PLAY_ICON;
      button.setAttribute('aria-label', on ? TEXTS.pause : TEXTS.play);
      wrap.classList.toggle('is-playing', on);
    };
    const pause = (): void => {
      if (!audio.paused) audio.pause();
    };
    players.add(pause);

    audio.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        durationMs = Math.round(audio.duration * 1000);
        opts.onDuration?.(durationMs);
      }
      show();
    });
    audio.addEventListener('timeupdate', show);
    audio.addEventListener('play', icon);
    audio.addEventListener('pause', icon);
    audio.addEventListener('ended', () => {
      audio.currentTime = 0;
      icon();
      show();
    });
    audio.addEventListener('error', () => {
      if (opts.fallback && !triedFallback) {
        triedFallback = true;
        audio.src = opts.fallback;
        return;
      }
      wrap.classList.add('is-broken');
      time.textContent = TEXTS.cantPlay;
    });
    button.addEventListener('click', () => {
      if (!audio.paused) {
        audio.pause();
        return;
      }
      stopPlayAll();
      pauseAll(pause);
      void audio.play().catch(() => {
        if (opts.fallback && !triedFallback) {
          triedFallback = true;
          audio.src = opts.fallback;
          void audio.play().catch(() => undefined);
        }
      });
    });
    seek.addEventListener('input', () => {
      dragging = true;
      if (durationMs) {
        const at = (Number(seek.value) / 1000) * durationMs;
        time.textContent = `${fmtMs(at)} / ${fmtMs(durationMs)}`;
      }
    });
    seek.addEventListener('change', () => {
      dragging = false;
      if (durationMs) audio.currentTime = ((Number(seek.value) / 1000) * durationMs) / 1000;
    });
    icon();
    show();
    return {
      element: wrap,
      pause,
      destroy: () => {
        pause();
        players.delete(pause);
        audio.removeAttribute('src');
        audio.load();
      },
    };
  }

  /** Builds a trimmer over a local blob and wires it to one selection key. */
  const attachBlobTrimmer = async (
    kind: AudioKind,
    key: string,
    host: HTMLElement,
    blob: Blob,
  ): Promise<WaveformTrimmer | null> => {
    const sel = selections.get(key);
    let cutter: WaveformTrimmer | null = null;
    const { onPlay } = joinPlayers(key, () => cutter);
    const built = await createWaveformTrimmer({
      blob,
      fadeChoicesMs: config.fadeChoicesMs,
      defaultFadeMs: config.defaultFadeMs,
      onRange: (fromMs, toMs) => patch(key, { fromMs, toMs }),
      onEffects: (effects: AudioEffects) => patch(key, chosenEffects(effects)),
      onPlay,
      initialEffects: sel,
      initialRange: sel ? { fromMs: sel.fromMs, toMs: sel.toMs } : undefined,
    });
    if (!built) return null;
    cutter = built;
    host.innerHTML = '';
    host.appendChild(built.element);
    onCleanup(key, () => built.destroy());
    return built;
  };

  /** Fetches a served file (AI voice, AI song, a kept recording) and opens the cutter over it. */
  const attachUrlTrimmer = async (kind: AudioKind, key: string, host: HTMLElement, url: string): Promise<boolean> => {
    try {
      const response = await fetch(url);
      if (!response.ok) return false;
      return (await attachBlobTrimmer(kind, key, host, await response.blob())) !== null;
    } catch {
      return false;
    }
  };

  /**
   * A finished sound: play it right away, cut it only on request. The cutter is
   * powerful but tall — nobody should scroll past it uninvited.
   */
  function soundRow(
    kind: AudioKind,
    key: string,
    src: string,
    opts: { knownMs?: number; fallback?: string; blob?: Blob; extra?: HTMLElement[]; onCut?: () => void } = {},
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pz-playback';
    const row = document.createElement('div');
    row.className = 'pz-playback-row';
    const player = makePlayer(src, {
      knownMs: opts.knownMs,
      fallback: opts.fallback,
      onDuration: (ms) => {
        const sel = selections.get(key);
        // Only for the sound this row plays — a take not chosen, or a new
        // recording not yet saved, must not rename the old one's length.
        const mine = sel && (sel.previewUrl === src || (sel.storageKey && fileUrl(sel.storageKey) === src));
        if (sel && mine && (!sel.durationMs || Math.abs(sel.durationMs - ms) > 400)) patch(key, { durationMs: ms });
      },
    });
    onCleanup(key, player.destroy);
    const cut = document.createElement('button');
    cut.type = 'button';
    cut.className = 'pz-btn-ghost small pz-cut-btn';
    cut.innerHTML = `${ICONS.scissors}<span>${TEXTS.cut}</span>`;
    row.append(player.element, cut, ...(opts.extra ?? []));
    const cutterHost = document.createElement('div');
    cut.addEventListener('click', () => {
      opts.onCut?.();
      cut.disabled = true;
      player.pause();
      const ready = opts.blob
        ? attachBlobTrimmer(kind, key, cutterHost, opts.blob).then((built) => built !== null)
        : attachUrlTrimmer(kind, key, cutterHost, opts.fallback ?? src);
      void ready.then((ok) => {
        if (ok) {
          // The cutter has its own player; two would fight.
          player.element.hidden = true;
          cut.hidden = true;
        } else {
          cut.disabled = false;
        }
      });
    });
    wrap.append(row, cutterHost);
    return wrap;
  }

  /** A progress box: real percent when known, staged text always. `label` replaces the percent. */
  function makeProgress(): {
    element: HTMLElement;
    show: (percent: number, stage: string, state?: 'busy' | 'done' | 'error', label?: string) => void;
    note: (text: string) => void;
    hide: () => void;
  } {
    const box = document.createElement('div');
    box.className = 'pz-prep-status';
    box.hidden = true;
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    const top = document.createElement('div');
    top.className = 'pz-prep-status-top';
    const stageEl = document.createElement('strong');
    const percentEl = document.createElement('span');
    top.append(stageEl, percentEl);
    const track = document.createElement('div');
    track.className = 'pz-prep-track';
    const fill = document.createElement('div');
    fill.className = 'pz-prep-fill';
    track.appendChild(fill);
    const noteEl = document.createElement('span');
    noteEl.className = 'pz-prep-note';
    box.append(top, track, noteEl);
    return {
      element: box,
      show: (percent, stage, state = 'busy', label) => {
        const safe = Math.min(100, Math.max(0, Math.round(percent)));
        box.hidden = false;
        box.className = `pz-prep-status is-${state}`;
        stageEl.textContent = stage;
        percentEl.textContent = label ?? `${safe}%`;
        fill.style.width = `${safe}%`;
      },
      note: (text) => {
        noteEl.textContent = text;
      },
      hide: () => {
        box.hidden = true;
      },
    };
  }

  /**
   * A quiet line about something loading in the background: a calm sentence
   * and a thin bar. The percent only moves the bar — a counting number reads
   * as something to wait for, and nothing here is.
   */
  function makeComing(): { element: HTMLElement; show: (text: string, percent?: number) => void; hide: () => void } {
    const line = document.createElement('p');
    line.className = 'pz-cut-coming pz-snd-coming';
    line.setAttribute('role', 'status');
    line.hidden = true;
    const text = document.createElement('span');
    const bar = document.createElement('span');
    bar.className = 'pz-cut-coming-bar';
    bar.setAttribute('aria-hidden', 'true');
    const fill = document.createElement('i');
    bar.appendChild(fill);
    line.append(text, bar);
    return {
      element: line,
      show: (words, percent) => {
        line.hidden = false;
        text.textContent = words;
        bar.hidden = percent == null;
        fill.style.width = `${Math.min(100, Math.max(0, Math.round(percent ?? 0)))}%`;
      },
      hide: () => {
        line.hidden = true;
      },
    };
  }

  /**
   * "Cela pesma ✓ · Iseci deo" over a cutter kept out of sight. The whole is
   * the answer from the first moment; "Iseci deo" simply shows the cutter,
   * which was built at once and whose file has been loading all along, so
   * everything is ready when it opens. "Cela pesma" takes the part back to the
   * whole and folds it away. A sound longer than the product plays has no
   * whole to offer: its cutter is open from the start on the part that fits.
   */
  function makePartChoice(opts: {
    host: HTMLElement;
    wholeLabel: string;
    cutter: () => WaveformTrimmer | null;
    /** The cutter was shown or folded away (by the shopper, or by `reset`). */
    onToggle?: (open: boolean) => void;
  }): { element: HTMLElement; sync: () => void; reset: (open: boolean) => void; isOpen: () => boolean } {
    const row = document.createElement('div');
    row.className = 'pz-part-choice';
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', TEXTS.partQuestion(opts.wholeLabel));
    row.hidden = true;
    const whole = document.createElement('button');
    whole.type = 'button';
    whole.className = 'pz-part-btn pz-part-whole';
    whole.innerHTML = `${ICONS.check}<span></span>`;
    whole.querySelector('span')!.textContent = opts.wholeLabel;
    const cut = document.createElement('button');
    cut.type = 'button';
    cut.className = 'pz-part-btn pz-part-cut';
    cut.innerHTML = `${ICONS.scissors}<span>${TEXTS.cutPart}</span>`;
    row.append(whole, cut);
    let open = false;
    let shownBefore = false;
    const shown = (): boolean => open || !(opts.cutter()?.wholeFits() ?? true);
    const sync = (): void => {
      const cutter = opts.cutter();
      const fits = cutter ? cutter.wholeFits() : true;
      const now = Boolean(cutter) && shown();
      // Too long to be whole: nothing to choose — the cutter is simply open,
      // saying why (its "Traje …, a proizvod pušta najviše …").
      row.hidden = !cutter || !fits;
      whole.hidden = !fits;
      opts.host.hidden = !now;
      whole.classList.toggle('is-active', !now);
      whole.setAttribute('aria-pressed', String(!now));
      cut.classList.toggle('is-active', now);
      cut.setAttribute('aria-pressed', String(now));
      if (now && !shownBefore) cutter?.relayout();
      if (now !== shownBefore) {
        shownBefore = now;
        opts.onToggle?.(now);
      }
    };
    whole.addEventListener('click', () => {
      const cutter = opts.cutter();
      if (cutter && !cutter.setWhole()) return;
      cutter?.pause();
      open = false;
      sync();
    });
    cut.addEventListener('click', () => {
      open = true;
      sync();
      opts.host.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    });
    return {
      element: row,
      sync,
      reset: (next) => {
        open = next;
        sync();
      },
      isOpen: () => Boolean(opts.cutter()) && shown(),
    };
  }

  /** Measures a local file's length, for the running total. */
  function measure(blob: Blob): Promise<number | undefined> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const probe = new Audio();
      const done = (ms?: number): void => {
        URL.revokeObjectURL(url);
        resolve(ms);
      };
      probe.preload = 'metadata';
      probe.addEventListener('loadedmetadata', () =>
        done(Number.isFinite(probe.duration) && probe.duration > 0 ? Math.round(probe.duration * 1000) : undefined),
      );
      probe.addEventListener('error', () => done(undefined));
      setTimeout(() => done(undefined), 8000);
      probe.src = url;
    });
  }

  // ── Editors ────────────────────────────────────────────────────────────────

  const builders: Record<AudioKind, (panel: HTMLElement, state: Panel, id: string) => void> = {
    song: buildSongPanel,
    voice: buildVoicePanel,
    tts: buildTtsPanel,
    youtube: buildYoutubePanel,
    tiktok: buildTiktokPanel,
    upload: buildUploadPanel,
  };

  // ── Restore: a draft or an "Izmeni" from the cart brings the sounds back ───
  const initial = (options.initial ?? []).filter(
    (sel) => hasSound(sel) && config.sources.includes(kindOf(sel.slot ?? sel.kind)),
  );
  if (initial.length > 0) {
    // Deferred a beat: emit() reaches the form's onChange, which must not run
    // while the form is still inside its own constructor.
    setTimeout(() => {
      for (const sel of initial) {
        const key = sel.slot ?? sel.kind;
        selections.set(key, { ...sel, slot: key, previewUrl: undefined });
        // The order they were committed in IS the order they play in.
        if (!order.includes(key)) order.push(key);
      }
      emit();
      if (single) openEditor(config.sources[0]!);
    }, 0);
  } else if (single) {
    // A single way of giving sound needs no tiles: its editor is the step.
    openEditor(config.sources[0]!);
  }
  if (single) addBox.hidden = true;
  // Drawn directly: emit() would reach the form's onChange from inside its constructor.
  renderTiles();
  renderOffer();
  // A Spotify song already picked (a draft, a reopened order): asked ahead
  // after the restored sounds are in, so a song already among them is not.
  if (matchAhead) setTimeout(askAhead, 0);
  if (config.sources.includes('upload')) options.registerImport?.(async file => {
    const sounds = arranged();
    const limit = Math.max(1, Math.min(MAX_SOUNDS, config.maxUploadFiles ?? 5));
    if ((!config.multiple && sounds.length) || sounds.length >= MAX_SOUNDS || sounds.filter(s => s.kind === 'upload').length >= limit) throw new Error('Uklonite postojeći zvuk ili izaberite drugo polje pre uvoza.');
    openEditor('upload');
    const accept = ensurePanel('upload').importFile;
    if (!accept) throw new Error('Polje za otpremanje zvuka nije spremno.');
    await accept(file);
  });
  return root;

  // ── AI song ────────────────────────────────────────────────────────────────
  function buildSongPanel(panel: HTMLElement, _state: Panel, id: string): void {
    const key = id;

    const form = document.createElement('div');
    form.className = 'pz-song-form';

    const styleLabel = document.createElement('p');
    styleLabel.className = 'pz-sub-label';
    styleLabel.textContent = TEXTS.songStyleLabel;
    const chips = document.createElement('div');
    chips.className = 'pz-chips';
    let chosenStyle = '';
    const customArea = document.createElement('textarea');
    customArea.className = 'pz-input';
    customArea.rows = 2;
    customArea.placeholder = TEXTS.songCustomPlaceholder;
    customArea.hidden = true;
    const setActiveChip = (button: HTMLButtonElement | null): void => {
      for (const el of chips.querySelectorAll('.pz-chip')) el.classList.remove('is-active');
      button?.classList.add('is-active');
    };
    for (const style of SONG_STYLES) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pz-chip';
      chip.textContent = style.label;
      chip.addEventListener('click', () => {
        chosenStyle = style.prompt;
        customArea.hidden = true;
        setActiveChip(chip);
      });
      chips.appendChild(chip);
    }
    const customChip = document.createElement('button');
    customChip.type = 'button';
    customChip.className = 'pz-chip';
    customChip.textContent = TEXTS.songCustomStyle;
    customChip.addEventListener('click', () => {
      chosenStyle = '';
      customArea.hidden = false;
      setActiveChip(customChip);
      customArea.focus();
    });
    chips.appendChild(customChip);

    const voiceLabel = document.createElement('p');
    voiceLabel.className = 'pz-sub-label';
    voiceLabel.textContent = TEXTS.songVoiceLabel;
    const genders = document.createElement('div');
    genders.className = 'pz-chips';
    let gender = 'm';
    for (const [value, label] of [
      ['m', TEXTS.songMale],
      ['f', TEXTS.songFemale],
    ] as const) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pz-chip' + (value === 'm' ? ' is-active' : '');
      chip.textContent = label;
      chip.addEventListener('click', () => {
        gender = value;
        for (const el of genders.querySelectorAll('.pz-chip')) el.classList.remove('is-active');
        chip.classList.add('is-active');
      });
      genders.appendChild(chip);
    }

    const lyricsLabel = document.createElement('p');
    lyricsLabel.className = 'pz-sub-label';
    lyricsLabel.textContent = TEXTS.songLyricsLabel;
    const lyrics = document.createElement('textarea');
    lyrics.className = 'pz-input';
    lyrics.rows = 5;
    lyrics.maxLength = 3000;
    lyrics.placeholder = TEXTS.songLyricsPlaceholder;
    const lyricsRow = document.createElement('div');
    lyricsRow.className = 'pz-song-lyrics-row';
    const counter = document.createElement('span');
    counter.className = 'pz-hint';
    counter.textContent = '0 / 3000';
    lyrics.addEventListener('input', () => {
      counter.textContent = `${lyrics.value.length} / 3000`;
    });
    const aiButton = document.createElement('button');
    aiButton.type = 'button';
    aiButton.className = 'pz-btn-ghost pz-song-ai';
    aiButton.textContent = TEXTS.songAiLyrics;
    aiButton.hidden = !config.lyricsAiAvailable;
    aiButton.addEventListener('click', () => {
      const description = lyrics.value.trim();
      if (description.length < 5) {
        status.textContent = 'Prvo opiši o čemu treba da bude pesma — pa klikni ponovo.';
        lyrics.focus();
        return;
      }
      aiButton.disabled = true;
      aiButton.textContent = TEXTS.songAiWorking;
      status.textContent = '';
      void api
        .songLyrics(description, customArea.hidden ? undefined : customArea.value.trim())
        .then((text) => {
          lyrics.value = text;
          counter.textContent = `${text.length} / 3000`;
        })
        .catch((err: Error) => {
          status.textContent = err.message;
        })
        .finally(() => {
          aiButton.disabled = false;
          aiButton.textContent = TEXTS.songAiLyrics;
        });
    });
    lyricsRow.append(aiButton, counter);

    const generate = document.createElement('button');
    generate.type = 'button';
    generate.className = 'pz-btn pz-song-generate';
    generate.textContent = TEXTS.songGenerate;

    const status = document.createElement('p');
    status.className = 'pz-hint pz-song-status';

    const progress = makeProgress();
    const results = document.createElement('div');
    results.className = 'pz-song-results';

    let clockTimer: number | undefined;
    let generation = 0;
    onCleanup(key, () => {
      generation++;
      if (clockTimer) window.clearInterval(clockTimer);
    });

    const stopLoaders = (): void => {
      if (clockTimer) window.clearInterval(clockTimer);
      clockTimer = undefined;
    };

    generate.addEventListener('click', () => {
      const style = customArea.hidden ? chosenStyle : customArea.value.trim();
      const text = lyrics.value.trim();
      if (style.length < 3) {
        status.textContent = TEXTS.songStyleMissing;
        return;
      }
      if (text.length < 20) {
        status.textContent = TEXTS.songLyricsMissing;
        lyrics.focus();
        return;
      }
      status.textContent = '';
      results.innerHTML = '';
      clearKey(key);
      generate.disabled = true;
      const run = ++generation;

      // The provider takes 1–3 minutes and reports two stages on the way:
      // the shopper sees which stage it is at and how long it has been — not
      // a percent nobody knows.
      const started = Date.now();
      let stage: keyof typeof SONG_STAGES = 'start';
      const draw = (): void => {
        const now = SONG_STAGES[stage];
        progress.show(now.at, now.text, 'busy', `korak ${now.step} od 3 · ${elapsedClock(Date.now() - started)}`);
      };
      draw();
      progress.note('Pesma se obično pravi 2–3 minuta. Možeš da nastaviš sa ostalim poljima — javićemo ti čim bude gotova.');
      clockTimer = window.setInterval(draw, 1000);

      void (async () => {
        try {
          const { id } = await api.songStart({ style, lyrics: text, voiceGender: gender });
          for (let attempt = 0; attempt < 60; attempt++) {
            await sleep(5000);
            if (run !== generation) return;
            let result;
            try {
              result = await api.songStatus(id);
            } catch {
              continue; // one flaky poll is not a failed song
            }
            if (result.phase && result.phase !== stage) {
              stage = result.phase;
              draw();
            }
            if (result.status === 'done' && result.songs.length > 0) {
              stopLoaders();
              progress.show(100, 'Pesma je spremna!', 'done');
              progress.note('');
              renderResults(result.songs);
              return;
            }
            if (result.status === 'error') {
              throw new Error(result.message ?? 'Pesma nije mogla da se napravi.');
            }
          }
          throw new Error('Generisanje je trajalo predugo — pokušaj ponovo.');
        } catch (err) {
          if (run !== generation) return;
          stopLoaders();
          progress.show(100, 'Nije uspelo', 'error');
          progress.note('');
          status.textContent = err instanceof Error ? err.message : 'Greška.';
        } finally {
          if (run === generation) generate.disabled = false;
        }
      })();
    });

    const renderResults = (songs: Array<{ id: string; title: string; url: string; storageKey?: string }>): void => {
      results.innerHTML = '';
      const heading = document.createElement('p');
      heading.className = 'pz-sub-label';
      heading.textContent = TEXTS.songPickVersion;
      results.appendChild(heading);

      const cutterHost = document.createElement('div');
      for (const song of songs) {
        const card = document.createElement('div');
        card.className = 'pz-song-version';
        const title = document.createElement('strong');
        title.textContent = song.title;
        const player = makePlayer(song.url, {
          onDuration: (ms) => {
            if (card.classList.contains('is-selected')) patch(key, { durationMs: ms });
            card.dataset.ms = String(ms);
          },
        });
        onCleanup(key, player.destroy);
        const select = document.createElement('button');
        select.type = 'button';
        select.className = 'pz-btn pz-song-select';
        select.textContent = TEXTS.songSelect;
        select.addEventListener('click', () => {
          for (const el of results.querySelectorAll('.pz-song-version')) {
            el.classList.remove('is-selected');
            const btn = el.querySelector('.pz-song-select') as HTMLButtonElement;
            btn.textContent = TEXTS.songSelect;
            btn.disabled = false;
          }
          card.classList.add('is-selected');
          select.textContent = TEXTS.songSelected;
          select.disabled = true;
          cutterHost.innerHTML = '';
          selections.delete(key);
          patch(key, {
            url: song.storageKey ? undefined : song.url,
            storageKey: song.storageKey,
            title: `AI pesma — ${song.title}`,
            previewUrl: song.url,
            durationMs: Number(card.dataset.ms) || undefined,
            fromMs: undefined,
            toMs: undefined,
          });
          cutRow.hidden = false;
        });
        card.append(title, player.element, select);
        results.appendChild(card);
      }

      const cutRow = document.createElement('div');
      cutRow.className = 'pz-row-inline';
      cutRow.hidden = true;
      const cutButton = document.createElement('button');
      cutButton.type = 'button';
      cutButton.className = 'pz-btn-ghost';
      cutButton.textContent = TEXTS.songCutOptional;
      cutButton.addEventListener('click', () => {
        const chosen = selections.get(key);
        const url = chosen?.previewUrl ?? chosen?.url ?? (chosen?.storageKey ? fileUrl(chosen.storageKey) : undefined);
        if (!url) return;
        cutButton.disabled = true;
        pauseAll();
        void attachUrlTrimmer('song', key, cutterHost, url).finally(() => {
          cutButton.disabled = false;
          cutRow.hidden = true;
        });
      });
      const again = document.createElement('button');
      again.type = 'button';
      again.className = 'pz-btn-ghost';
      again.textContent = TEXTS.songAgain;
      again.addEventListener('click', () => {
        results.innerHTML = '';
        progress.hide();
        clearKey(key);
      });
      cutRow.append(cutButton, again);
      results.append(cutRow, cutterHost);
    };

    form.append(styleLabel, chips, customArea, voiceLabel, genders, lyricsLabel, lyrics, lyricsRow, generate, status);
    panel.append(keptBlock('song', key), form, progress.element, results);
  }

  /**
   * A sound restored from a draft or the cart, in the editor that made it: it
   * plays from the stored copy and can still be cut. Empty when there is none.
   */
  function keptBlock(kind: AudioKind, key: string, extra: HTMLElement[] = []): HTMLElement {
    const box = document.createElement('div');
    box.className = 'pz-kept';
    const sel = selections.get(key);
    const src = sel?.previewUrl ?? (sel?.storageKey ? fileUrl(sel.storageKey) : undefined);
    if (!sel || !src || !hasSound(sel)) {
      box.hidden = true;
      return box;
    }
    const label = document.createElement('p');
    label.className = 'pz-sub-label';
    label.textContent = `${TEXTS.kept}: ${soundLabel(sel)}`;
    box.append(label, soundRow(kind, key, src, { knownMs: sel.durationMs, extra }));
    keptBoxes.set(key, { box, storageKey: sel.storageKey });
    return box;
  }

  // ── Own voice recording ────────────────────────────────────────────────────
  //
  // The recording plays the moment it stops, from this browser's copy, while it
  // is saved in the background — nobody waits on an upload to hear themselves.
  // A live level meter shows the microphone is really hearing them, and a take
  // that carried no sound (the wrong microphone, a muted headset, a recorder
  // that delivered nothing) is refused with a word why, instead of being kept
  // as a silent "✓ snimljeno" the product would play as nothing.
  //
  // Every take owns its microphone, recorder, pieces and timers, and only one
  // take exists at a time. A second tap while a headset was still switching on
  // used to start a second recorder beside the first: both wrote into one list
  // of pieces, the first was never stopped (the timer kept counting, the
  // microphone stayed on), and the next take began with the stray recorder's
  // headerless piece — a file neither the browser nor the server could open.
  // An upload that finishes after a newer take was made changes nothing.
  function buildVoicePanel(panel: HTMLElement, state: Panel, id: string): void {
    const key = id;
    const maxSeconds = Math.max(10, Math.min(Math.round(rules.limitMs / 1000), config.maxRecordSeconds ?? 600));

    const stage = document.createElement('div');
    stage.className = 'pz-rec-stage';
    const recordButton = document.createElement('button');
    recordButton.type = 'button';
    recordButton.className = 'pz-rec-btn';
    recordButton.innerHTML = MIC_ICON;
    recordButton.setAttribute('aria-label', TEXTS.startRecording);
    const readout = document.createElement('div');
    readout.className = 'pz-rec-readout';
    const timer = document.createElement('div');
    timer.className = 'pz-rec-timer';
    timer.textContent = '0:00';
    const levels = document.createElement('div');
    levels.className = 'pz-rec-level';
    const levelBars: HTMLElement[] = [];
    for (let i = 0; i < 16; i++) {
      const bar = document.createElement('span');
      levels.appendChild(bar);
      levelBars.push(bar);
    }
    readout.append(timer, levels);
    stage.append(recordButton, readout);

    const hint = document.createElement('p');
    hint.className = 'pz-hint';
    const recordHint = TEXTS.recordHint(Math.round(maxSeconds / 60));
    hint.textContent = recordHint;
    const status = document.createElement('p');
    status.className = 'pz-hint pz-rec-status';
    status.setAttribute('role', 'status');
    const result = document.createElement('div');
    result.className = 'pz-rec-result';

    // Which microphone. A computer often has several — the webcam's, a
    // headset, "Stereo Mix", a virtual one — and the browser's default can be
    // one that sends pure silence: the take then came out empty with no way to
    // pick another. The list appears once the browser may name them (after the
    // first permission), and the choice is remembered.
    const micRow = document.createElement('div');
    micRow.className = 'pz-rec-mic';
    micRow.hidden = true;
    const micLabel = document.createElement('label');
    micLabel.className = 'pz-rec-mic-pick';
    const micCaption = document.createElement('span');
    micCaption.textContent = TEXTS.micPick;
    const micSelect = document.createElement('select');
    micSelect.className = 'pz-input';
    micLabel.append(micCaption, micSelect);
    const checkBtn = document.createElement('button');
    checkBtn.type = 'button';
    checkBtn.className = 'pz-btn-ghost small';
    checkBtn.textContent = TEXTS.micCheck;
    micRow.append(micLabel, checkBtn);

    /** One recording, from the tap that asked for the microphone to its file. */
    interface Take {
      phase: 'opening' | 'recording' | 'stopping' | 'ended';
      /** When the microphone was asked for; a hung permission prompt can be tapped away. */
      askedAt: number;
      stream?: MediaStream;
      recorder?: MediaRecorder;
      meter?: Meter;
      /** This take's pieces — never shared with another recorder. */
      chunks: Blob[];
      startedAt: number;
      stoppedAt: number;
      tick?: number;
      limit?: number;
      /** Waits for a `stop` event some browsers never send. */
      watchdog?: number;
      forLength: boolean;
      quietWarned: boolean;
      /** Thrown away (the editor closed before it began, the sound removed): its file is not kept. */
      discarded: boolean;
    }
    /** The take being made; null between takes. */
    let take: Take | null = null;
    /** The editor was thrown away: nothing may touch the answer or the page any more. */
    let disposed = false;
    /** The newest save; an older one that finishes late is ignored. */
    let saveSeq = 0;
    let saving = false;
    /** The running level meter of a microphone check. */
    let meter: Meter | null = null;
    /** The stream of a running microphone check. */
    let checkStream: MediaStream | null = null;

    const savedMic = (): string | undefined => {
      try {
        return localStorage.getItem(MIC_KEY) || undefined;
      } catch {
        return undefined;
      }
    };
    const saveMic = (id: string): void => {
      try {
        if (id) localStorage.setItem(MIC_KEY, id);
        else localStorage.removeItem(MIC_KEY);
      } catch {
        /* a convenience only */
      }
    };

    /** The microphone's stream: the chosen one, else the browser's default. */
    const openMic = async (): Promise<MediaStream> => {
      const wanted = micSelect.value || savedMic();
      const base = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      if (wanted) {
        try {
          return await navigator.mediaDevices.getUserMedia({ audio: { ...base, deviceId: { exact: wanted } } });
        } catch (err) {
          // A microphone unplugged since: the default, and forget the choice.
          const name = (err as Error | undefined)?.name;
          if (name !== 'OverconstrainedError' && name !== 'NotFoundError') throw err;
          saveMic('');
        }
      }
      return navigator.mediaDevices.getUserMedia({ audio: base });
    };

    /** Lists the microphones once the browser will name them; shows the picker when there is a choice. */
    const listMics = async (stream?: MediaStream, force = false): Promise<void> => {
      try {
        const inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
          (d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'communications',
        );
        if (disposed) return;
        const current = stream?.getAudioTracks()[0]?.getSettings().deviceId || micSelect.value || savedMic() || '';
        micSelect.innerHTML = '';
        inputs.forEach((d, i) => {
          const item = document.createElement('option');
          item.value = d.deviceId === 'default' ? '' : d.deviceId;
          item.textContent = d.label || `${TEXTS.micPick} ${i + 1}`;
          micSelect.appendChild(item);
        });
        if ([...micSelect.options].some((o) => o.value === current)) micSelect.value = current;
        // No names yet (the browser has not been allowed): the check alone.
        micLabel.hidden = inputs.length === 0;
        micRow.hidden = !(force || inputs.filter((d) => d.deviceId !== 'default').length > 1);
      } catch {
        /* no list: the default it is */
      }
    };

    const setStatus = (text: string, error = false): void => {
      status.textContent = text;
      status.classList.toggle('is-error', error);
    };

    /** Something went wrong with the microphone: say so, and offer the picker and the check. */
    const micTrouble = (text: string): void => {
      setStatus(text, true);
      void listMics(undefined, true);
    };

    const stopCheck = (): void => {
      if (!checkStream) return;
      meter?.stop();
      meter = null;
      for (const track of checkStream.getTracks()) track.stop();
      checkStream = null;
      checkBtn.textContent = TEXTS.micCheck;
      stage.classList.remove('is-checking');
    };

    /** The stage between takes: the microphone to tap, the check allowed again. */
    const showIdle = (): void => {
      stage.classList.remove('is-recording', 'is-opening');
      recordButton.innerHTML = MIC_ICON;
      recordButton.setAttribute('aria-label', TEXTS.startRecording);
      recordButton.removeAttribute('aria-busy');
      checkBtn.disabled = false;
      result.hidden = false;
    };

    /** Lets a take go: its microphone, meter and timers. Safe to call twice. */
    const release = (t: Take): void => {
      if (t.tick !== undefined) window.clearInterval(t.tick);
      if (t.limit !== undefined) window.clearTimeout(t.limit);
      if (t.watchdog !== undefined) window.clearTimeout(t.watchdog);
      t.tick = t.limit = t.watchdog = undefined;
      t.meter?.stop();
      for (const track of t.stream?.getTracks() ?? []) track.stop();
    };

    /** Ends a recording; its file is made when the recorder has handed over its last piece. */
    const finishTake = (t: Take): void => {
      if (t.phase !== 'recording') return;
      t.phase = 'stopping';
      t.stoppedAt = Date.now();
      if (t.tick !== undefined) window.clearInterval(t.tick);
      if (t.limit !== undefined) window.clearTimeout(t.limit);
      t.tick = t.limit = undefined;
      // A recorder that never says it stopped still ends the take.
      t.watchdog = window.setTimeout(() => takeEnded(t), 4000);
      try {
        if (t.recorder && t.recorder.state !== 'inactive') t.recorder.stop();
        else takeEnded(t);
      } catch {
        takeEnded(t);
      }
    };

    /**
     * Stops whatever this editor is doing with the microphone. `keep`: a take
     * being recorded ends the way the stop button ends it and is saved; a take
     * whose microphone is still switching on is dropped (it never began).
     */
    const halt = (keep: boolean): void => {
      stopCheck();
      const t = take;
      if (!t) return;
      if (t.phase === 'recording' || t.phase === 'stopping') {
        if (!keep) t.discarded = true;
        finishTake(t);
        return;
      }
      if (t.phase === 'opening') {
        t.discarded = true;
        take = null;
        t.phase = 'ended';
        release(t);
        if (!disposed) {
          showIdle();
          setStatus('');
        }
      }
    };

    // Leaving the step, or this editor for another, ends a recording the way
    // the stop button does — the take is kept — and switches a check off.
    const stopMic = (): void => halt(true);
    stops.add(stopMic);
    state.halt = () => halt(true);
    state.busy = () => take !== null || saving;
    onCleanup(key, () => {
      disposed = true;
      stops.delete(stopMic);
      halt(false);
      saveSeq++;
      meter?.stop();
    });

    // "Proveri mikrofon": the bars move while you talk — try another
    // microphone until they do, then record.
    checkBtn.addEventListener('click', async () => {
      if (take) return;
      if (checkStream) {
        stopCheck();
        return;
      }
      const context = newContext();
      let stream: MediaStream;
      try {
        stream = await openMic();
      } catch {
        void context?.close().catch(() => undefined);
        if (!disposed) micTrouble(TEXTS.micDenied);
        return;
      }
      // A take started (or the editor went) while the check was opening.
      if (disposed || take || checkStream) {
        for (const track of stream.getTracks()) track.stop();
        void context?.close().catch(() => undefined);
        return;
      }
      checkStream = stream;
      void listMics(stream, true);
      meter = startMeter(stream, context, levelBars);
      stage.classList.add('is-checking');
      checkBtn.textContent = TEXTS.micCheckStop;
      setStatus(TEXTS.micChecking);
    });
    micSelect.addEventListener('change', () => {
      saveMic(micSelect.value);
      if (checkStream) {
        stopCheck();
        checkBtn.click();
      }
    });

    /** Why a take was not saved, in words that say what to do, and whether trying again can help. */
    const saveTrouble = (err: unknown): { text: string; retry: boolean } => {
      const failure = err instanceof UploadError ? err : null;
      if (failure?.status === 415) return { text: TEXTS.takeBroken, retry: false };
      if (failure?.status === 413) return { text: TEXTS.takeTooBig, retry: false };
      if (failure?.kind === 'busy') return { text: TEXTS.saveBusy, retry: true };
      return { text: TEXTS.saveFailed, retry: true };
    };

    /**
     * A take that came to nothing after its editor was closed (Nazad while
     * recording): an editor left without a sound goes, as closing it would
     * have done. True when it went.
     */
    const dropIfClosed = (): boolean => {
      if (single || openKind === id || panelHasSound(id)) return false;
      destroyPanel(id);
      return true;
    };

    /** The last take saved as this editor's sound, whose local copy goes when a newer one replaces it. */
    let answerUrl: string | null = null;

    /** Saves the take; a failed save keeps the take here with a retry. */
    const save = (blob: Blob, recordedMs: number, localUrl: string): void => {
      const mine = ++saveSeq;
      saving = true;
      const hadAnswer = selections.has(key);
      setStatus(TEXTS.saving);
      const extension = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      void api
        .upload(blob, `poruka.${extension}`)
        .then((uploaded) => {
          if (disposed || mine !== saveSeq) return;
          saving = false;
          // A new take being recorded keeps its own words on screen.
          if (!take) setStatus(TEXTS.saved);
          const replaced = answerUrl;
          answerUrl = localUrl;
          patch(key, {
            storageKey: uploaded.storageKey,
            title: 'Glasovna poruka',
            // What the recorder counted. The server treats it as a hint it may
            // believe up to the product's own limit, never past it; the local
            // URL below never leaves this browser.
            durationMs: recordedMs || undefined,
            previewUrl: localUrl,
            // A new take is a new sound: the old take's cut and fades do not fit it.
            fromMs: undefined,
            toMs: undefined,
          });
          if (replaced && replaced !== localUrl) URL.revokeObjectURL(replaced);
        })
        .catch((err: unknown) => {
          if (disposed || mine !== saveSeq) return;
          saving = false;
          // A new take is already being recorded: it replaces this one anyway.
          if (take) return;
          const trouble = saveTrouble(err);
          setStatus(hadAnswer ? `${trouble.text} ${TEXTS.previousKept}` : trouble.text, true);
          if (trouble.retry) {
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'pz-btn-ghost small';
            retry.textContent = TEXTS.retrySave;
            retry.addEventListener('click', () => {
              retry.remove();
              save(blob, recordedMs, localUrl);
            });
            status.appendChild(retry);
          }
          // Closed while it was saving: the editor comes back to say so,
          // unless the shopper is busy in another one (its tile reopens it).
          if (openKind === null && !panelHasSound(id)) openEditor(id);
          renderEditorFoot();
        });
      renderEditorFoot();
    };

    /** "Obriši": this voice message goes — the saved one, a take still saving, all of it. */
    const removeMessage = (): void => {
      halt(false);
      saveSeq++;
      saving = false;
      if (selections.has(key)) {
        removeSound(key);
        return;
      }
      result.innerHTML = '';
      timer.textContent = '0:00';
      setStatus('');
      renderEditorFoot();
    };

    const deleteButton = (): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-btn-ghost small pz-rec-delete';
      button.innerHTML = `${ICONS.close}<span>${TEXTS.deleteTake}</span>`;
      button.addEventListener('click', removeMessage);
      return button;
    };

    const showTake = (blob: Blob, recordedMs: number): void => {
      const localUrl = URL.createObjectURL(blob);
      onCleanup(key, () => URL.revokeObjectURL(localUrl));
      result.innerHTML = '';
      const again = document.createElement('button');
      again.type = 'button';
      again.className = 'pz-btn-ghost small';
      again.textContent = TEXTS.reRecord;
      again.addEventListener('click', () => {
        if (!take) recordButton.click();
      });
      result.appendChild(
        soundRow('voice', key, localUrl, { knownMs: recordedMs, blob, extra: [again, deleteButton()] }),
      );
      save(blob, recordedMs, localUrl);
    };

    /** The recorder handed over its last piece (or never will): the take becomes a file, or a reason. */
    const takeEnded = (t: Take): void => {
      if (t.phase === 'ended') return;
      t.phase = 'ended';
      if (!t.stoppedAt) t.stoppedAt = Date.now();
      // Silence is only claimed on real evidence: a running meter that read
      // the microphone many times and never heard anything.
      const heard = t.meter && t.meter.readings() >= 8 ? t.meter.peak() : null;
      release(t);
      if (take === t) take = null;
      if (disposed) return;
      showIdle();
      if (t.discarded) {
        setStatus('');
        return;
      }
      const type = t.recorder?.mimeType || t.chunks[0]?.type || 'audio/webm';
      const blob = new Blob(t.chunks, { type });
      // How long the recording ran. A recorded webm often has no length in
      // its header, so this counter is the only length there is until the
      // worker probes the file.
      const recordedMs = t.startedAt > 0 ? t.stoppedAt - t.startedAt : 0;
      timer.textContent = fmtMs(recordedMs);
      if (recordedMs < 800 || blob.size < 1500 || (heard !== null && heard < SILENT_PEAK)) {
        if (dropIfClosed()) return;
      }
      if (recordedMs < 800) {
        setStatus(TEXTS.tooShort, true);
        return;
      }
      if (blob.size < 1500) {
        micTrouble(TEXTS.micEmpty);
        return;
      }
      if (heard !== null && heard < SILENT_PEAK) {
        micTrouble(TEXTS.micSilent);
        return;
      }
      if (t.forLength) hint.textContent = TEXTS.tooLong(Math.round(maxSeconds / 60));
      // A file that does not open the way a recording opens would only be
      // refused by the server after its upload: said here, at once.
      void recordingHeaderOk(blob).then((ok) => {
        if (disposed || take) return;
        if (!ok) {
          if (dropIfClosed()) return;
          setStatus(TEXTS.takeBroken, true);
          return;
        }
        showTake(blob, recordedMs);
      });
    };

    recordButton.addEventListener('click', async () => {
      if (disposed) return;
      const current = take;
      if (current) {
        if (current.phase === 'recording') {
          // The second half of a double tap is not "stop".
          if (Date.now() - current.startedAt < DOUBLE_TAP_MS) return;
          finishTake(current);
        } else if (current.phase === 'opening' && Date.now() - current.askedAt > MIC_PATIENCE_MS) {
          // A permission prompt left unanswered: tapping again starts over.
          halt(false);
        }
        // Still switching the microphone on, or already stopping: one take at a time.
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setStatus(TEXTS.micUnsupported, true);
        return;
      }
      pauseAll();
      stopPlayAll();
      stopCheck();
      const t: Take = {
        phase: 'opening',
        askedAt: Date.now(),
        chunks: [],
        startedAt: 0,
        stoppedAt: 0,
        forLength: false,
        quietWarned: false,
        discarded: false,
      };
      take = t;
      stage.classList.add('is-opening');
      recordButton.setAttribute('aria-busy', 'true');
      checkBtn.disabled = true;
      hint.textContent = recordHint;
      setStatus(TEXTS.micOpening);
      // Made inside the tap: a phone keeps an audio context created later
      // suspended, and a suspended meter would read every take as silence.
      const context = newContext();
      let stream: MediaStream;
      try {
        stream = await openMic();
      } catch {
        void context?.close().catch(() => undefined);
        if (take !== t) return;
        take = null;
        t.phase = 'ended';
        if (disposed) return;
        showIdle();
        micTrouble(TEXTS.micDenied);
        return;
      }
      t.stream = stream;
      // Given up on while the microphone was switching on.
      if (take !== t || t.phase !== 'opening' || disposed) {
        void context?.close().catch(() => undefined);
        release(t);
        return;
      }
      void listMics(stream);

      let recorder: MediaRecorder;
      try {
        // Safari produces MP4 and refuses an explicit webm type, so the
        // browser's own default is used and the server accepts either.
        recorder = new MediaRecorder(stream);
      } catch {
        void context?.close().catch(() => undefined);
        take = null;
        t.phase = 'ended';
        release(t);
        showIdle();
        setStatus(TEXTS.micUnsupported, true);
        return;
      }
      t.recorder = recorder;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) t.chunks.push(event.data);
      });
      recorder.addEventListener('stop', () => takeEnded(t));
      recorder.addEventListener('error', () => {
        if (t.phase === 'recording') finishTake(t);
      });

      setStatus(TEXTS.recordingNow);
      const track = stream.getAudioTracks()[0];
      // A muted or dead track records nothing at all: say so at once.
      if (!track || track.readyState === 'ended' || track.muted) {
        t.quietWarned = true;
        micTrouble(TEXTS.micQuietLive);
      }
      // The live meter — and the evidence that the take carries sound. Two
      // seconds of nothing while recording is said right away, not after.
      t.meter = startMeter(stream, context, levelBars, () => {
        if (t.quietWarned || t.phase !== 'recording' || take !== t) return;
        t.quietWarned = true;
        micTrouble(TEXTS.micQuietLive);
      });

      try {
        // A slice every second: a take cut short by a crash or a closed tab
        // still has most of its sound, and no browser holds it all in one piece.
        recorder.start(1000);
      } catch {
        take = null;
        t.phase = 'ended';
        release(t);
        showIdle();
        setStatus(TEXTS.micUnsupported, true);
        return;
      }
      t.phase = 'recording';
      t.startedAt = Date.now();
      stage.classList.remove('is-opening');
      stage.classList.add('is-recording');
      recordButton.removeAttribute('aria-busy');
      recordButton.innerHTML = STOP_ICON;
      recordButton.setAttribute('aria-label', TEXTS.stopRecording);
      // The old take's player is not what is being made now.
      result.hidden = true;
      timer.textContent = '0:00';
      t.tick = window.setInterval(() => {
        if (take === t) timer.textContent = fmtMs(Date.now() - t.startedAt);
      }, 250);
      // A forgotten recording would otherwise run until the tab closes.
      t.limit = window.setTimeout(() => {
        t.forLength = true;
        finishTake(t);
      }, maxSeconds * 1000);
    });

    // A browser that already allowed the microphone names them now.
    void navigator.permissions
      ?.query({ name: 'microphone' as PermissionName })
      .then((permission) => {
        if (permission.state === 'granted') void listMics();
      })
      .catch(() => undefined);

    panel.append(keptBlock('voice', key, [deleteButton()]), stage, hint, status, micRow, result);
  }

  // ── AI voice (TTS) ─────────────────────────────────────────────────────────
  //
  // Two readings of the same text in the same voice, side by side: an AI voice
  // stresses a sentence differently each time, and the shopper hears both and
  // keeps the one that sounds like what they meant. The first is chosen at
  // once, so the step is answered even if they never compare.
  function buildTtsPanel(panel: HTMLElement, _state: Panel, id: string): void {
    const key = id;
    const maxChars = config.ttsMaxChars ?? 300;
    const voices = config.ttsVoices ?? [];

    if (config.ttsExampleUrl) {
      const exampleLabel = document.createElement('p');
      exampleLabel.className = 'pz-sub-label';
      exampleLabel.textContent = TEXTS.ttsExample;
      const example = makePlayer(config.ttsExampleUrl);
      onCleanup(key, example.destroy);
      panel.append(exampleLabel, example.element);
    }

    let voiceSelect: HTMLSelectElement | undefined;
    if (voices.length > 1) {
      const voiceLabel = document.createElement('p');
      voiceLabel.className = 'pz-sub-label';
      voiceLabel.textContent = TEXTS.ttsVoiceLabel;
      voiceSelect = document.createElement('select');
      voiceSelect.className = 'pz-input';
      for (const voice of voices) {
        const item = document.createElement('option');
        item.value = voice.id;
        item.textContent = voice.label;
        voiceSelect.appendChild(item);
      }
      panel.append(voiceLabel, voiceSelect);
    }

    const text = document.createElement('textarea');
    text.className = 'pz-input';
    text.rows = 3;
    text.maxLength = maxChars;
    text.placeholder = TEXTS.ttsPlaceholder;
    const counter = document.createElement('span');
    counter.className = 'pz-hint';
    counter.textContent = `0 / ${maxChars}`;
    text.addEventListener('input', () => {
      counter.textContent = `${text.value.length} / ${maxChars}`;
      if (selections.has(key)) generate.textContent = TEXTS.ttsRegenerate;
    });

    const generate = document.createElement('button');
    generate.type = 'button';
    generate.className = 'pz-btn';
    generate.textContent = TEXTS.ttsGenerate;
    const status = document.createElement('p');
    status.className = 'pz-hint';
    const takesBox = document.createElement('div');
    takesBox.className = 'pz-song-results';

    generate.addEventListener('click', () => {
      const value = text.value.trim();
      if (value.length < 2) {
        status.textContent = TEXTS.ttsMissing;
        text.focus();
        return;
      }
      generate.disabled = true;
      status.textContent = TEXTS.ttsWorking;
      takesBox.innerHTML = '';
      pauseAll();
      void api
        .tts(value, voiceSelect?.value, 2)
        .then((result) => {
          const title = `AI glasovna: „${value.slice(0, 60)}${value.length > 60 ? '…' : ''}"`;
          status.textContent = result.takes.length > 1 ? TEXTS.ttsPick : '';
          const cards: Array<{ card: HTMLElement; button: HTMLButtonElement }> = [];
          const choose = (index: number): void => {
            const take = result.takes[index]!;
            cards.forEach((entry, i) => {
              entry.card.classList.toggle('is-selected', i === index);
              entry.button.textContent = i === index ? TEXTS.ttsChosen : TEXTS.ttsChoose;
              entry.button.disabled = i === index;
            });
            selections.delete(key);
            patch(key, {
              storageKey: take.storageKey,
              title,
              previewUrl: take.url,
              durationMs: Number(cards[index]?.card.dataset.ms) || undefined,
            });
          };
          result.takes.forEach((take, index) => {
            const card = document.createElement('div');
            card.className = 'pz-song-version';
            const name = document.createElement('strong');
            name.textContent = result.takes.length > 1 ? TEXTS.ttsTake(index + 1) : title;
            const row = soundRow('tts', key, take.url, { onCut: () => choose(index) });
            // The length arrives with the file; the chosen take reports it.
            const probe = new Audio();
            probe.preload = 'metadata';
            probe.addEventListener('loadedmetadata', () => {
              if (!Number.isFinite(probe.duration)) return;
              card.dataset.ms = String(Math.round(probe.duration * 1000));
              if (card.classList.contains('is-selected')) patch(key, { durationMs: Number(card.dataset.ms) });
            });
            probe.src = take.url;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pz-btn pz-song-select';
            button.addEventListener('click', () => choose(index));
            button.hidden = result.takes.length < 2;
            card.append(name, row, button);
            takesBox.appendChild(card);
            cards.push({ card, button });
          });
          choose(0);
          generate.textContent = TEXTS.ttsRegenerate;
        })
        .catch((err: Error) => {
          status.textContent = err.message;
        })
        .finally(() => {
          generate.disabled = false;
        });
    });

    panel.append(keptBlock('tts', key), text, counter, generate, status, takesBox);
  }

  // ── YouTube ────────────────────────────────────────────────────────────────
  //
  // A link plays straight away in YouTube's own player; a name is searched and
  // the results listed. The pick becomes a card — the video, its name,
  // "Promeni pesmu" — over the cutter, which works from the first moment: the
  // song's length is known from the search, so the part is marked while
  // YouTube's player loads, and the song plays through that player until its
  // own file arrives (see "Download first" below). A typed name alone is still
  // a valid answer.
  //
  // A video whose owner forbids playing it on other sites is still a valid
  // song: the cutter keeps working on its known length, other versions that DO
  // play are offered beside it, and meanwhile the server fetches the audio —
  // when it arrives the cutter plays the song's own file.
  function buildYoutubePanel(panel: HTMLElement, state: Panel, id: string): void {
    const key = id;
    const manualOnly = config.youtubeMode === 'manual';

    const searchRow = document.createElement('div');
    searchRow.className = 'pz-range pz-audio-search';
    const linkInput = document.createElement('input');
    linkInput.type = 'text';
    linkInput.className = 'pz-input';
    linkInput.style.flex = '1';
    linkInput.placeholder = TEXTS.linkPlaceholder;
    linkInput.enterKeyHint = 'search';
    const findButton = document.createElement('button');
    findButton.type = 'button';
    findButton.className = 'pz-btn';
    findButton.style.padding = '9px 16px';
    findButton.textContent = TEXTS.find;
    searchRow.append(linkInput, findButton);
    if (manualOnly) findButton.hidden = true;

    const status = document.createElement('p');
    status.className = 'pz-hint';
    const results = document.createElement('div');
    results.className = 'pz-yt-results';
    results.hidden = true;
    const altBox = document.createElement('div');
    altBox.className = 'pz-yt-alt';
    altBox.hidden = true;

    // The picked song: its card — the video, its name, "Promeni pesmu" — and
    // the cutter under it.
    const pickBox = document.createElement('div');
    pickBox.className = 'pz-yt-pick';
    pickBox.hidden = true;
    const card = document.createElement('div');
    card.className = 'pz-yt-song';
    const cardMedia = document.createElement('div');
    cardMedia.className = 'pz-yt-song-media';
    const cardText = document.createElement('div');
    cardText.className = 'pz-yt-song-text';
    const cardTitle = document.createElement('strong');
    cardTitle.className = 'pz-yt-song-title';
    const cardMeta = document.createElement('span');
    cardMeta.className = 'pz-yt-song-meta';
    const changeBtn = document.createElement('button');
    changeBtn.type = 'button';
    changeBtn.className = 'pz-btn-ghost small pz-yt-change';
    changeBtn.textContent = TEXTS.changeSong;
    cardText.append(cardTitle, cardMeta, changeBtn);
    card.append(cardMedia, cardText);
    const blockedNote = document.createElement('p');
    blockedNote.className = 'pz-hint pz-yt-blocked';
    blockedNote.hidden = true;
    // The pick is the answer at once, and the card says so: the whole song,
    // "možeš odmah dalje". The cutter is built at the same moment (its song
    // loading behind it) and waits folded away until "Iseci deo".
    const doneLine = document.createElement('p');
    doneLine.className = 'pz-snd-done-line';
    doneLine.setAttribute('role', 'status');
    doneLine.hidden = true;
    const lookLine = document.createElement('p');
    lookLine.className = 'pz-hint pz-yt-looking';
    lookLine.textContent = OFFER_TEXTS.looking;
    lookLine.hidden = true;
    const cutHost = document.createElement('div');
    cutHost.className = 'pz-yt-cutter';
    cutHost.hidden = true;
    pickBox.append(card, blockedNote, doneLine, lookLine, cutHost);

    // Which part of the song, in words — the answer when no video is picked
    // (a typed name, another site's link, manual mode).
    const partChips = document.createElement('div');
    partChips.className = 'pz-chips';
    let part = 'cela';
    const partDefs = [
      ['cela', 'Cela pesma'],
      ['refren', 'Refren'],
      ['deo', 'Deo pesme'],
    ] as const;
    const range = document.createElement('div');
    range.className = 'pz-range';
    const from = document.createElement('input');
    const to = document.createElement('input');
    for (const [input, label] of [
      [from, TEXTS.from],
      [to, TEXTS.to],
    ] as const) {
      input.type = 'text';
      input.inputMode = 'decimal';
      input.className = 'pz-input pz-input-small';
      input.placeholder = label === TEXTS.from ? '0:00' : '1:30';
      input.setAttribute('aria-label', label);
      input.addEventListener('input', () => {
        if (!video) patch(key, { fromMs: parseTime(from.value), toMs: parseTime(to.value) });
      });
    }
    range.append(from, document.createTextNode('–'), to);
    range.hidden = true;
    const rangeHint = document.createElement('p');
    rangeHint.className = 'pz-hint';
    rangeHint.textContent = TEXTS.rangeHint;
    rangeHint.hidden = true;
    const manualBox = document.createElement('div');
    manualBox.append(partChips, range, rangeHint);

    /** The video in the editor, when one is picked. */
    let video: { id: string; title?: string; durationMs?: number; channel?: string } | null = null;
    /**
     * The Spotify song this editor is looking for ("Tvoja pesma"), and the
     * search text it filled in. The sound then carries Spotify's own title,
     * artist and id, whichever video plays it — until the shopper types
     * something else, which is then their own song.
     */
    let song: PickedSong | null = null;
    let songQuery = '';
    /**
     * "Tvoja pesma" → Da, before its video is known: the Spotify song is the
     * answer by its name, and the card shows it as done meanwhile.
     */
    let pending: PickedSong | null = null;
    /**
     * What the match for the pending song came back with, when it brought no
     * video: likely versions to choose from, or nothing (the name stays).
     */
    let pendingSettled: 'choose' | 'none' | null = null;
    /** "Koja je prava verzija?": the likely versions of the Spotify song, as the match listed them. */
    let songChoices: YoutubeVideo[] = [];
    let yt: YoutubePlayer | null = null;
    let cutter: WaveformTrimmer | null = null;
    /** The part the pick opens on (a restored song's), until its cutter exists. */
    let openingRange: { fromMs?: number; toMs?: number } | undefined;
    /** The fetch the match asked ahead already started for a video, by its link. */
    let knownPrep: { url: string; prepId: string } | null = null;
    /**
     * The cutter was opened because the sounds together ran too long — once
     * per pick, so a shopper who folds it again ("Cela pesma") is not overruled.
     */
    let openedForOver = false;
    state.clip = () => yt;
    const partPick = makePartChoice({
      host: cutHost,
      wholeLabel: TEXTS.wholeSong,
      cutter: () => cutter,
      onToggle: () => renderDone(),
    });
    doneLine.after(partPick.element);

    /**
     * The card's "done" line: the whole song, or the part the shopper marked —
     * "možeš odmah dalje" only while everything together fits. Past the limit
     * it says calmly what to cut, and opens the cutter when there is one.
     * Runs after every change to any sound (`mixWatchers`).
     */
    function renderDone(): void {
      const sel = selections.get(key);
      const over = overFor(key);
      if (!over) openedForOver = false;
      const waiting = Boolean(pending && !video);
      let text = '';
      if (waiting) {
        text = over
          ? overPrompt({
              maxMs: rules.limitMs,
              alone: over.alone,
              sound: 'song',
              how: !over.known ? 'others' : pendingSettled === 'choose' ? 'choose' : pendingSettled === 'none' ? 'words' : 'later',
            })
          : OFFER_TEXTS.added;
        // No video to cut, and too long whole: the part in words is the way.
        if (!manualOnly) manualBox.hidden = !(over?.known && pendingSettled === 'none');
        // The line over the versions (or the name-only hint) follows too — a
        // search the shopper ran meanwhile keeps its own words.
        if (pendingSettled && [OFFER_TEXTS.choose, OFFER_TEXTS.chooseShort, TEXTS.nameOnly, ''].includes(status.textContent ?? '')) {
          status.textContent =
            pendingSettled === 'choose'
              ? over?.known
                ? OFFER_TEXTS.chooseShort
                : OFFER_TEXTS.choose
              : manualOnly || over?.known
                ? ''
                : TEXTS.nameOnly;
        }
      } else if (video && sel?.url) {
        if (over) {
          text = overPrompt({ maxMs: rules.limitMs, alone: over.alone, sound: 'song', how: over.known ? 'cut' : 'others' });
          // Opened only in the editor on screen: another song's editor keeps
          // its "Iseci deo" for the shopper to choose there.
          if (cutter && over.known && !openedForOver && openKind === key) {
            openedForOver = true;
            partPick.reset(true);
          }
        } else if (blockedNote.hidden) {
          // A song that cannot play here says it in its own calm line instead.
          text = sel.fromMs == null && sel.toMs == null ? TEXTS.ytWholeDone : TEXTS.ytPartDone;
        }
      }
      doneLine.textContent = text;
      doneLine.hidden = !text;
      doneLine.classList.toggle('is-cut', Boolean(text && over));
      // "Tvoja pesma" before its video: the optional choice of versions sits
      // under the card that says the song is in, not above it.
      if (waiting !== (status.parentElement === pickBox)) {
        if (waiting) cutHost.before(status, results);
        else pickBox.before(status, results);
      }
    }
    mixWatchers.add(renderDone);
    onCleanup(key, () => mixWatchers.delete(renderDone));
    // The video and the cutter are one player on the step: the cutter plays
    // THROUGH the video until the song's own file is here, so starting one of
    // them must never pause the other.
    const { onPlay: songPlays } = joinPlayers(key, () => ({
      pause: () => {
        cutter?.pause();
        yt?.pause();
      },
    }));

    const typedTitle = (value: string): string => `${value}${part === 'refren' ? ' — refren' : ''}`;

    /** The answer when no video is picked: the typed name or link, in words. */
    const applyTyped = (): void => {
      if (video) return;
      const value = linkInput.value.trim();
      const hasValue = value.length > 0;
      manualBox.hidden = !hasValue && !manualOnly;
      range.hidden = part !== 'deo';
      rangeHint.hidden = part !== 'deo';
      if (!hasValue) {
        clearKey(key);
        return;
      }
      const isLink = /^https?:\/\//i.test(value);
      patch(key, {
        url: isLink ? value : undefined,
        storageKey: undefined,
        previewUrl: undefined,
        // The Spotify song given by name: its own title and artist, which the
        // worker searches together after the order.
        title: isLink ? undefined : song ? typedTitle(song.title) : typedTitle(value),
        artist: isLink ? undefined : song?.artists,
        spotifyUri: isLink ? undefined : song?.uri,
        // The whole Spotify song runs as long as Spotify says: the length bar
        // and Premium are right before any video is chosen.
        durationMs: !isLink && song && part === 'cela' ? song.durationMs : undefined,
        fromMs: part === 'deo' ? parseTime(from.value) : undefined,
        toMs: part === 'deo' ? parseTime(to.value) : undefined,
      });
    };

    for (const [value, label] of partDefs) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pz-chip' + (value === 'cela' ? ' is-active' : '');
      chip.textContent = label;
      chip.addEventListener('click', () => {
        part = value;
        // The Spotify song's "cela pesma" card gives way to the part in words.
        if (pending && !video) {
          pending = null;
          pendingSettled = null;
          pickBox.hidden = true;
          clearPlayer();
          renderDone();
        }
        for (const el of partChips.querySelectorAll('.pz-chip')) el.classList.remove('is-active');
        chip.classList.add('is-active');
        if (part !== 'deo') {
          from.value = '';
          to.value = '';
        }
        applyTyped();
      });
      partChips.appendChild(chip);
    }

    /** Bumped by every pick and check, so a late answer about an old video is dropped. */
    let checkSeq = 0;
    const clearPlayer = (): void => {
      cutter?.destroy();
      cutter = null;
      yt?.hint.remove();
      yt?.destroy();
      yt = null;
      cardMedia.innerHTML = '';
      card.classList.remove('is-plain', 'is-heard', 'is-pending');
      cutHost.innerHTML = '';
      blockedNote.hidden = true;
      blockedNote.textContent = '';
      lookLine.hidden = true;
      altBox.hidden = true;
      altBox.innerHTML = '';
      openingRange = undefined;
      openedForOver = false;
      partPick.sync();
    };
    onCleanup(key, () => {
      checkSeq++;
      prefetch = null;
      pending = null;
      clearPlayer();
    });
    /**
     * "Promeni pesmu": back to the search. The song stays the answer — its
     * name, its part, its file — until another one is picked or typed: turning
     * it into the bare link in the field lost all three, and a shopper who
     * looked and kept it (or saved an edit from the cart meanwhile) got the
     * song back nameless and whole.
     */
    const unpick = (): void => {
      clearPlayer();
      video = null;
      pending = null;
      pendingSettled = null;
      prefetch = null;
      checkSeq++;
      pickBox.hidden = true;
      renderDone();
      // A link in the field (a restored song) is not a search: the field
      // starts empty. Search words stay, with their results.
      if (/^https?:\/\//i.test(linkInput.value.trim())) linkInput.value = '';
      refreshUseSong();
    };

    /** The card's words: the video's name, its channel and length. */
    const showCard = (): void => {
      if (!video) return;
      cardTitle.textContent = video.title ?? '';
      cardMeta.textContent = [video.channel, video.durationMs ? fmtMs(video.durationMs) : null].filter(Boolean).join(' · ');
      pickBox.hidden = false;
      renderDone();
    };

    /**
     * "Tvoja pesma" → Da: the Spotify song on the card at once — its sleeve,
     * name and length, "✓ Pesma je dodata" — while its video is looked up.
     */
    const showSongCard = (picked: PickedSong): void => {
      clearPlayer();
      video = null;
      pending = picked;
      card.classList.add('is-pending');
      const art = document.createElement(picked.cover ? 'img' : 'span');
      art.className = 'pz-yt-song-cover';
      if (art instanceof HTMLImageElement) {
        art.src = picked.cover!;
        art.alt = '';
      } else art.innerHTML = ICONS.music;
      cardMedia.appendChild(art);
      cardTitle.textContent = picked.title;
      cardMeta.textContent = [picked.artists, picked.durationMs ? fmtMs(picked.durationMs) : null].filter(Boolean).join(' · ');
      lookLine.hidden = false;
      pickBox.hidden = false;
      renderDone();
    };

    /** The cutter, the moment the song's length is known — out of sight until "Iseci deo". */
    const ensureCutter = (durationMs: number | undefined): void => {
      if (cutter || !video || !durationMs || durationMs <= 0) return;
      const sel = selections.get(key);
      const opening = openingRange;
      cutter = createPendingCutter({
        pending: { durationMs },
        transport: yt && !yt.isPlain() ? yt : null,
        wholeSong: true,
        wholeLabel: TEXTS.wholeSong,
        maxMs: rules.limitMs,
        // A song longer than the product plays opens on its first allowed
        // stretch — unless the shopper already chose a part of it.
        fitToMax: opening?.fromMs == null && opening?.toMs == null,
        initialRange: opening,
        initialEffects: sel,
        fadeChoicesMs: config.fadeChoicesMs,
        defaultFadeMs: config.defaultFadeMs,
        onRange: (fromMs, toMs) => {
          patch(key, { fromMs, toMs });
          renderDone();
        },
        onEffects: (effects: AudioEffects) => patch(key, chosenEffects(effects)),
        onPlay: songPlays,
        // The song's own file plays from here on: the video steps back to its still.
        onAudioTakesOver: () => yt?.retire(),
      });
      if (!cutter) return;
      cutHost.appendChild(cutter.element);
      // Folded away unless a part was already chosen (a restored song) or the
      // whole song is longer than the product plays (then it opens on its own).
      partPick.sync();
      if (prefetch?.state === 'running') progress(prefetch, 3);
      renderDone();
    };

    // ── Download first ──────────────────────────────────────────────────────
    //
    // The moment a video is chosen the server starts fetching the song itself.
    // Nothing waits for it: the pick is the answer at once, the cutter is
    // there at once (the length is known from the search), and YouTube's
    // player plays the song meanwhile. When the file arrives the order carries
    // it, the cutter draws its real wave and plays it, and "Preslušaj sve"
    // plays it too. An order placed before that is fine as well: the order's
    // audio job finds this same fetch by its link (or fetches the song itself)
    // and cuts it as marked. A fetch that fails is said kindly: the song is
    // chosen, production gets it.

    type Ready = { storageKey?: string; url: string; durationMs?: number; peaks?: number[]; blob?: Blob };
    /** The fetch for the video in the editor; `wave`: the shopper needs it to hear the song. */
    let prefetch: { id: string; url: string; state: 'running' | 'done' | 'failed'; ready?: Ready; wave: boolean } | null = null;
    /** Other uploads of the Spotify song ("Tvoja pesma"), tried when one cannot be fetched. */
    let alternates: YoutubeVideo[] = [];

    /** How far the song's own file is: one quiet line in the cutter, its bar moving. */
    const progress = (job: NonNullable<typeof prefetch>, percent: number): void => {
      if (prefetch !== job) return;
      const text = job.wave ? TEXTS.ytPreparing : TEXTS.ytBackground;
      if (cutter) cutter.setStatus({ text, percent, tone: 'busy' });
      else if (job.wave) status.textContent = text;
    };

    const startPrefetch = (id: string, url: string, wave: boolean): void => {
      if (manualOnly) return;
      if (prefetch?.id === id) {
        prefetch.wave ||= wave;
        if (!wave) return;
        if (prefetch.state === 'done') void takeAudio(prefetch, prefetch.ready!);
        else if (prefetch.state === 'failed') prepFailed(true);
        else progress(prefetch, 10);
        return;
      }
      const job = { id, url, state: 'running' as 'running' | 'done' | 'failed', wave } as NonNullable<typeof prefetch>;
      prefetch = job;
      progress(job, 3);
      void download(job);
    };

    const download = async (job: NonNullable<typeof prefetch>): Promise<void> => {
      const kept = selections.get(key);
      const keptSrc = kept?.url === job.url && kept.storageKey ? fileUrl(kept.storageKey) : undefined;
      try {
        let ready: Ready | null = null;
        if (keptSrc) {
          // Fetched before (a restored song): the stored copy, if it is still there.
          const response = await fetch(keptSrc).catch(() => null);
          if (response?.ok) ready = { storageKey: kept!.storageKey, url: keptSrc, durationMs: kept!.durationMs, blob: await response.blob() };
        }
        if (!ready) {
          // The match asked ahead may have started this very fetch: preparing
          // finds it again (and costs nothing); its id is kept for when the
          // request itself is refused (a spent rate limit).
          const ahead = knownPrep?.url === job.url ? knownPrep.prepId : undefined;
          const jobId = await api.prepareAudio(job.url).then(
            (started) => started.id,
            (error: unknown) => {
              if (ahead) return ahead;
              throw error;
            },
          );
          for (let attempt = 0; attempt < 150 && !ready; attempt++) {
            if (attempt > 0) await sleep(2000);
            if (prefetch !== job) return;
            const row = await api.prepareStatus(jobId);
            if (row.status === 'error') throw new Error(row.error ?? 'error');
            progress(job, Number.isFinite(row.progress) ? Math.max(3, row.progress) : 10);
            if (row.status === 'done' && row.url && row.durationMs) {
              ready = { storageKey: row.storageKey ?? undefined, url: row.url, durationMs: row.durationMs, peaks: row.peaks };
            }
          }
        }
        if (!ready) throw new Error('timeout');
        if (prefetch !== job) return;
        job.state = 'done';
        job.ready = ready;
        // The order carries the file from here on, and "Preslušaj sve" plays it.
        patch(key, {
          storageKey: ready.storageKey,
          previewUrl: ready.url,
          durationMs: ready.durationMs ?? selections.get(key)?.durationMs,
        });
        await takeAudio(job, ready);
      } catch {
        if (prefetch !== job) return;
        job.state = 'failed';
        // The Spotify song: another upload of the same recording, while the
        // shopper has not cut this one — for a whole song they are the same.
        const sel = selections.get(key);
        const next = alternates.shift();
        if (next && song && sel?.fromMs == null && sel?.toMs == null) {
          pick({ id: next.id, title: next.title, durationMs: next.durationMs, channel: next.channel }, undefined, true, partPick.isOpen());
          cutter?.setStatus({ text: TEXTS.ytTryingOther, tone: 'busy' });
          return;
        }
        prepFailed(job.wave || !yt?.ready());
      }
    };

    /** The song's own file, in the cutter: its wave and its sound. */
    const takeAudio = async (job: NonNullable<typeof prefetch>, ready: Ready): Promise<void> => {
      if (prefetch !== job) return;
      if (!cutter) ensureCutter(ready.durationMs ?? video?.durationMs);
      const ok = cutter
        ? await cutter.setAudio({ peaks: ready.peaks, durationMs: ready.durationMs, audioUrl: ready.url, blob: ready.blob })
        : false;
      if (prefetch !== job) return;
      if (!ok) {
        prepFailed(true);
        return;
      }
      blockedNote.hidden = true;
      status.textContent = '';
      // The song plays here now after all: the video's "does not play here" goes.
      card.classList.add('is-heard');
      cutter!.setStatus({ text: job.wave ? TEXTS.ytPrepared : TEXTS.ytReady, tone: 'done' });
      partPick.sync();
      renderDone();
    };

    /** Not fetched (yet): the song is chosen all the same, and said so kindly. */
    const prepFailed = (cannotHear: boolean): void => {
      const text = cannotHear ? TEXTS.ytPrepFailed : TEXTS.ytChosen;
      if (cutter) cutter.setStatus({ text, tone: 'note' });
      else status.textContent = text;
      // A song that cannot play here said "we are preparing it": no longer true.
      if (!blockedNote.hidden) blockedNote.textContent = TEXTS.ytPrepFailed;
    };

    const resultRow = (one: YoutubeVideo): HTMLButtonElement => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'pz-yt-result';
      const img = document.createElement('img');
      img.src = one.thumbnail;
      img.alt = '';
      img.loading = 'lazy';
      const text = document.createElement('span');
      text.className = 'pz-yt-result-text';
      const name = document.createElement('strong');
      name.textContent = one.title;
      const meta = document.createElement('span');
      meta.textContent = [one.channel, one.durationMs ? fmtMs(one.durationMs) : null].filter(Boolean).join(' · ');
      text.append(name, meta);
      // Looking for the Spotify song: the upload of its length is the likeliest right one.
      if (song && linkInput.value.trim() === songQuery && sameLength(one, song)) {
        const same = document.createElement('span');
        same.className = 'pz-snd-offer-same';
        same.textContent = `✓ ${OFFER_TEXTS.sameLength}`;
        text.appendChild(same);
      }
      const choose = document.createElement('span');
      choose.className = 'pz-yt-result-pick';
      choose.textContent = TEXTS.pickResult;
      row.append(img, text, choose);
      row.addEventListener('click', () => {
        if (linkInput.value.trim() !== songQuery) song = null;
        // One of the likely versions of the Spotify song, of its length: the
        // other copies of that recording stand by in case this upload cannot
        // be fetched, as for a sure match (see `useVideo`). A version of
        // another length is a concert or another cut — nothing stands in for it.
        const picked = song;
        alternates =
          picked && sameLength(one, picked) && songChoices.some((v) => v.id === one.id)
            ? songChoices.filter((v) => v.id !== one.id && sameLength(v, picked)).slice(0, 2)
            : [];
        pick({ id: one.id, title: one.title, durationMs: one.durationMs, channel: one.channel }, undefined, true);
      });
      return row;
    };

    /**
     * The picked video cannot play here. It stays the answer: the part is
     * chosen on the cutter over its known length, versions that do play are
     * offered, and the audio is fetched so it can be heard after all.
     */
    const showBlocked = (
      chosen: { id: string },
      info: { title?: string; video?: YoutubeVideo; videos: YoutubeVideo[] },
    ): void => {
      if (video?.id !== chosen.id) return;
      const current = selections.get(key);
      const title = info.video?.title ?? info.title ?? video.title;
      const durationMs = info.video?.durationMs || video.durationMs || current?.durationMs;
      video = { ...video, title, durationMs, channel: info.video?.channel ?? video.channel };
      cutter?.setTransport(null);
      yt?.retire(true);
      blockedNote.textContent = TEXTS.ytBlocked;
      blockedNote.hidden = false;
      const url = `https://www.youtube.com/watch?v=${chosen.id}`;
      patch(key, { url, title: song?.title ?? title, durationMs });
      showCard();
      if (!cutter) {
        openingRange = current?.fromMs != null || current?.toMs != null ? { fromMs: current.fromMs, toMs: current.toMs } : undefined;
        if (openingRange) partPick.reset(true);
        ensureCutter(durationMs);
      }
      altBox.innerHTML = '';
      const others = info.videos.filter((v) => v.id !== chosen.id);
      if (others.length > 0) {
        const heading = document.createElement('p');
        heading.className = 'pz-sub-label';
        heading.textContent = TEXTS.ytAlternatives;
        altBox.append(heading, ...others.map(resultRow));
        altBox.hidden = false;
      }
      startPrefetch(chosen.id, url, true);
    };

    const whenBlocked = async (chosen: { id: string; title?: string }, known?: {
      title?: string;
      video?: YoutubeVideo;
      videos: YoutubeVideo[];
    }): Promise<void> => {
      const seq = ++checkSeq;
      let found = known;
      if (!found) {
        // The player refused, often before the check below answered: ask the
        // check itself, which names the video, gives its length and finds the
        // versions that play. (A search would not: it leaves blocked videos
        // out.) When the check still calls it playable — an age or region
        // limit — the other versions are searched by name.
        const title = chosen.title ?? video?.title;
        const checked = await api.checkYoutube(chosen.id, title);
        if (!checked.embeddable) {
          found = { title: checked.title, video: checked.video, videos: checked.videos };
        } else {
          const name = title ?? checked.title;
          const searched = name ? await api.searchYoutube(name) : { videos: [] };
          found = { title: name, videos: searched.videos };
        }
      }
      if (seq !== checkSeq) return;
      showBlocked(chosen, found);
    };

    const pick = (
      chosen: { id: string; title?: string; durationMs?: number; channel?: string },
      initial?: { fromMs?: number; toMs?: number },
      /** A search result: the server already checked that it plays. */
      checked = false,
      /** Another upload of the same song stands in: the cutter stays open if it was. */
      keepCut = false,
    ): void => {
      clearPlayer();
      video = { ...chosen };
      pending = null;
      pendingSettled = null;
      results.hidden = true;
      status.textContent = '';
      manualBox.hidden = true;
      // The whole song until the shopper asks to cut — a restored part opens cut.
      partPick.reset(keepCut || initial?.fromMs != null || initial?.toMs != null);
      const url = `https://www.youtube.com/watch?v=${chosen.id}`;
      const kept = selections.get(key);
      const sameSong = kept?.url === url;
      patch(key, {
        url,
        storageKey: sameSong ? kept?.storageKey : undefined,
        previewUrl: undefined,
        title: song?.title ?? chosen.title,
        artist: song?.artists,
        spotifyUri: song?.uri,
        durationMs: chosen.durationMs || undefined,
        fromMs: initial?.fromMs,
        toMs: initial?.toMs,
      });
      openingRange = initial;
      const player = createYoutubePlayer({
        videoId: chosen.id,
        title: chosen.title,
        onPlay: songPlays,
        onBlocked: () => void whenBlocked(chosen),
        onReady: (info) => {
          if (video?.id !== chosen.id) return;
          // A pasted video is named and measured only once the player loads:
          // kept, so a refusal after that can still look the song up by name.
          if (info.title && !video.title) {
            video.title = info.title;
            if (!song) patch(key, { title: info.title });
          }
          if (!video.durationMs && info.durationMs) {
            video.durationMs = info.durationMs;
            patch(key, { durationMs: info.durationMs });
          }
          showCard();
          ensureCutter(video.durationMs);
        },
      });
      yt = player;
      // YouTube's script did not load: its plain player has its own controls,
      // needs the card's full width, and cannot be driven from the cutter.
      player.subscribe(() => {
        if (!player.isPlain() || card.classList.contains('is-plain')) return;
        card.classList.add('is-plain');
        cutter?.setTransport(null);
      });
      cardMedia.appendChild(player.frame);
      card.after(player.hint);
      showCard();
      ensureCutter(chosen.durationMs);
      // Download first: the song's own file, whatever the player does.
      startPrefetch(chosen.id, url, false);
      if (!checked) {
        // A pasted link can be one its owner will not let play on other sites:
        // ask before the shopper meets a player that cannot play.
        const seq = ++checkSeq;
        void api.checkYoutube(chosen.id, chosen.title).then((result) => {
          if (seq !== checkSeq) return;
          if (result.embeddable) {
            // The check measured it: the cutter need not wait for the player.
            if (video?.id === chosen.id && !video.durationMs && result.video?.durationMs) {
              video.durationMs = result.video.durationMs;
              if (!video.title && result.video.title) video.title = result.video.title;
              patch(key, { durationMs: video.durationMs });
              showCard();
              ensureCutter(video.durationMs);
            }
            return;
          }
          void whenBlocked(chosen, { title: result.title, video: result.video, videos: result.videos });
        });
      }
    };

    changeBtn.addEventListener('click', () => {
      unpick();
      if (results.childElementCount > 0) results.hidden = false;
      linkInput.focus();
    });

    const showResults = (videos: YoutubeVideo[]): void => {
      results.innerHTML = '';
      for (const one of videos) results.appendChild(resultRow(one));
      results.hidden = videos.length === 0;
    };

    let searchSeq = 0;
    const find = async (): Promise<void> => {
      const value = linkInput.value.trim();
      if (value.length < 2) return;
      const id = parseYoutubeId(value);
      if (id) {
        pick({ id });
        return;
      }
      if (/^https?:\/\//i.test(value)) {
        // Another site's link: kept as the answer, fetched after the order.
        status.textContent = TEXTS.otherLink;
        return;
      }
      const seq = ++searchSeq;
      findButton.disabled = true;
      status.textContent = TEXTS.searching;
      const { videos, code } = await api.searchYoutube(value);
      findButton.disabled = false;
      if (seq !== searchSeq) return;
      if (code || videos.length === 0) {
        status.textContent =
          code === 'rate_limited' ? TEXTS.ytRateLimited : code === 'no_results' || !code ? TEXTS.ytNoResults : TEXTS.ytFailed;
        return;
      }
      status.textContent = '';
      // A new search while a song plays offers the list; the playing song
      // stays the answer until another one is chosen.
      showResults(videos);
    };
    findButton.addEventListener('click', () => void find());

    // The song already picked on Spotify for this product, offered in one tap:
    // it fills the search with "title artist" and runs it.
    const useSong = document.createElement('button');
    useSong.type = 'button';
    useSong.className = 'pz-chip pz-audio-use-song';
    useSong.hidden = true;
    const refreshUseSong = (): void => {
      const picked = options.pickedSong?.current();
      const query = picked ? [picked.title, picked.artists].filter(Boolean).join(' ').trim() : '';
      useSong.hidden = !query || linkInput.value.trim() === query;
      useSong.textContent = picked ? `${TEXTS.usePickedSong}: ${[picked.title, picked.artists].filter(Boolean).join(' – ')}` : '';
      useSong.dataset.query = query;
    };
    useSong.addEventListener('click', () => {
      const query = useSong.dataset.query ?? '';
      const picked = options.pickedSong?.current();
      if (!query || !picked) return;
      useSong.hidden = true;
      state.useQuery?.(query, picked);
    });
    options.pickedSong?.subscribe(refreshUseSong);
    refreshUseSong();

    linkInput.addEventListener('input', () => {
      // Typing something else makes it the shopper's own song, not Spotify's.
      if (song && linkInput.value.trim() !== songQuery) song = null;
      // …and the Spotify song's card ("Pesma je dodata") gives way to it.
      if (pending && !song) {
        pending = null;
        pendingSettled = null;
        pickBox.hidden = true;
        clearPlayer();
        renderDone();
      }
      refreshUseSong();
      const value = linkInput.value.trim();
      // A pasted YouTube link plays at once — no button needed.
      const id = manualOnly ? null : parseYoutubeId(value);
      if (id) {
        if (video?.id !== id) pick({ id });
        return;
      }
      if (video) return; // The playing pick stays until another is chosen.
      status.textContent = value.length >= 2 && !manualOnly && !/^https?:/i.test(value) ? TEXTS.nameOnly : '';
      applyTyped();
    });
    linkInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (!manualOnly) void find();
      }
    });

    panel.append(searchRow, useSong, status, results, pickBox, altBox, manualBox);
    manualBox.hidden = !manualOnly;

    // "Tvoja pesma": the step found the Spotify song, or asks to search for it.
    // Found: the video plays at once, as the whole song. Not found: the name
    // is already the answer (the worker finds it after the order) and the
    // search runs, so the shopper can still listen and choose.
    state.useVideo = (one, picked, query, others, prepId) => {
      song = picked;
      songQuery = query;
      // Only copies of the same recording (Spotify's length): a failed fetch
      // must never swap in a concert or another cut of the song.
      alternates = (others ?? []).filter((v) => v.id !== one.id && sameLength(v, picked)).slice(0, 2);
      knownPrep = prepId ? { url: `https://www.youtube.com/watch?v=${one.id}`, prepId } : null;
      linkInput.value = query;
      refreshUseSong();
      results.hidden = true;
      status.textContent = '';
      pick({ id: one.id, title: one.title, durationMs: one.durationMs, channel: one.channel }, undefined, true);
    };
    // "Da": the whole song by its Spotify name is the answer this instant —
    // the card says so, and nothing is waited for. Its video follows.
    state.useSong = (picked) => {
      const query = [picked.title, picked.artists.split(',')[0]?.trim()].filter(Boolean).join(' ');
      song = picked;
      songQuery = query;
      alternates = [];
      songChoices = [];
      pendingSettled = null;
      knownPrep = null;
      prefetch = null;
      checkSeq++;
      linkInput.value = query;
      refreshUseSong();
      results.hidden = true;
      results.innerHTML = '';
      status.textContent = '';
      // Manual mode keeps its "Cela pesma / Refren / Deo pesme" words.
      manualBox.hidden = !manualOnly;
      part = 'cela';
      for (const chip of partChips.querySelectorAll<HTMLElement>('.pz-chip')) {
        chip.classList.toggle('is-active', chip.textContent === partDefs[0][1]);
      }
      range.hidden = true;
      rangeHint.hidden = true;
      patch(key, songAnswer(picked));
      showSongCard(picked);
    };
    // The match arrived (usually long before "Da", asked ahead): a sure one
    // becomes the video in place; an unsure one lists the likely versions,
    // which the shopper may pick or ignore; none leaves the name as it is.
    // (Past the product's limit the card says what to do instead — see renderDone.)
    state.upgradeSong = (picked, match) => {
      // Moved on meanwhile: a video picked, a name typed, the song changed.
      if (!pending || songKey(pending) !== songKey(picked) || video || linkInput.value.trim() !== songQuery) return;
      lookLine.hidden = true;
      const plan = matchPlan(match, picked);
      if ('take' in plan) {
        state.useVideo?.(plan.take, picked, songQuery, match.candidates, match.prepId);
      } else if ('choose' in plan) {
        pendingSettled = 'choose';
        songChoices = plan.choose;
        status.textContent = OFFER_TEXTS.choose;
        showResults(plan.choose);
        renderDone();
      } else {
        pendingSettled = 'none';
        status.textContent = manualOnly ? '' : TEXTS.nameOnly;
        renderDone();
      }
    };
    state.useQuery = (query, picked) => {
      song = picked;
      songQuery = query;
      linkInput.value = query;
      linkInput.dispatchEvent(new Event('input'));
      if (!manualOnly) void find();
    };

    // A restored song: the kept link opens on the kept part; a kept name comes
    // back into the field with its od–do.
    const kept = selections.get(key);
    if (kept?.spotifyUri) {
      song = {
        title: (kept.title ?? '').replace(/ — refren$/i, ''),
        artists: kept.artist ?? '',
        uri: kept.spotifyUri,
        durationMs: kept.durationMs,
      };
    }
    if (kept) {
      const keptId = kept.url ? parseYoutubeId(kept.url) : null;
      if (keptId && !manualOnly) {
        linkInput.value = kept.url!;
        songQuery = linkInput.value;
        pick({ id: keptId, title: kept.title, durationMs: kept.durationMs }, { fromMs: kept.fromMs, toMs: kept.toMs });
      } else if (kept.url || kept.title) {
        linkInput.value = kept.url ?? (kept.title ?? '').replace(/ — refren$/i, '');
        songQuery = linkInput.value.trim();
        if (/ — refren$/i.test(kept.title ?? '')) part = 'refren';
        if (kept.fromMs != null || kept.toMs != null) {
          part = 'deo';
          from.value = kept.fromMs != null ? fmtMs(kept.fromMs) : '';
          to.value = kept.toMs != null ? fmtMs(kept.toMs) : '';
        }
        for (const chip of partChips.querySelectorAll<HTMLElement>('.pz-chip')) {
          chip.classList.toggle('is-active', chip.textContent === partDefs.find(([v]) => v === part)?.[1]);
        }
        applyTyped();
      }
    }
  }

  // ── TikTok ─────────────────────────────────────────────────────────────────
  //
  // A pasted link is the answer at once ("✓ Link je sačuvan — možeš odmah
  // dalje"), and its sound starts loading by itself a moment later. The server
  // names and measures it within seconds, so the cutter is there — folded away
  // behind "Iseci deo" — long before the file; when the file lands it plays,
  // and the cutter draws its real wave. "Učitaj zvuk" is only a retry. A post
  // locked at the source is the one link that is not an answer: the shopper is
  // asked for another.
  function buildTiktokPanel(panel: HTMLElement, _state: Panel, id: string): void {
    const key = id;

    const row = document.createElement('div');
    row.className = 'pz-range pz-audio-search';
    const linkInput = document.createElement('input');
    linkInput.type = 'text';
    linkInput.className = 'pz-input';
    linkInput.style.flex = '1';
    linkInput.inputMode = 'url';
    linkInput.placeholder = TEXTS.tiktokPlaceholder;
    const loadButton = document.createElement('button');
    loadButton.type = 'button';
    loadButton.className = 'pz-btn';
    loadButton.style.padding = '9px 16px';
    loadButton.textContent = TEXTS.loadSound;
    loadButton.hidden = true;
    row.append(linkInput, loadButton);

    const hint = document.createElement('p');
    hint.className = 'pz-hint';
    hint.textContent = TEXTS.tiktokHint;
    const savedLine = document.createElement('p');
    savedLine.className = 'pz-snd-done-line';
    savedLine.setAttribute('role', 'status');
    savedLine.hidden = true;
    const coming = makeComing();
    const locked = document.createElement('div');
    locked.className = 'pz-prep-status is-error pz-tt-locked';
    locked.setAttribute('role', 'alert');
    locked.hidden = true;
    const lockedTitle = document.createElement('strong');
    lockedTitle.textContent = TEXTS.tiktokLocked;
    const lockedNote = document.createElement('span');
    lockedNote.className = 'pz-prep-note';
    lockedNote.textContent = TEXTS.tiktokLockedNote;
    locked.append(lockedTitle, lockedNote);
    const playerRow = document.createElement('div');
    playerRow.className = 'pz-playback-row';
    playerRow.hidden = true;
    const cutterHost = document.createElement('div');
    cutterHost.className = 'pz-tt-cutter';
    cutterHost.hidden = true;

    let cutter: WaveformTrimmer | null = null;
    let player: ReturnType<typeof makePlayer> | null = null;
    const { onPlay } = joinPlayers(key, () => cutter);

    /** The sound on its way, as the panel last heard: one quiet line while the cutter is folded. */
    let comingNow: { text: string; percent?: number } | null = null;
    const renderComing = (): void => {
      // An open cutter says it under its own wave: one line, never two.
      if (comingNow && !partPick.isOpen()) coming.show(comingNow.text, comingNow.percent);
      else coming.hide();
    };
    const setComing = (next: { text: string; percent?: number } | null): void => {
      comingNow = next;
      renderComing();
    };

    /** What the saved line says while everything together fits ("možeš odmah dalje"); '' hides it. */
    let savedText = '';
    /** The cutter was opened because the sounds together ran too long (once, see the YouTube editor). */
    let openedForOver = false;
    /**
     * The saved line: the link is the answer — "možeš odmah dalje" only while
     * everything together fits. Past the limit it says calmly what to cut, and
     * opens the cutter when there is one. Runs after every change to any sound.
     */
    const renderSaved = (): void => {
      const over = savedText ? overFor(key) : null;
      if (!over) openedForOver = false;
      const text = over
        ? overPrompt({ maxMs: rules.limitMs, alone: over.alone, sound: 'tiktok', how: over.known && cutter ? 'cut' : 'others' })
        : savedText;
      if (over && over.known && cutter && !openedForOver && openKind === key) {
        openedForOver = true;
        partPick.reset(true);
      }
      savedLine.textContent = text;
      savedLine.hidden = !text;
      savedLine.classList.toggle('is-cut', Boolean(over));
    };
    const setSaved = (text: string): void => {
      savedText = text;
      renderSaved();
    };

    const partPick = makePartChoice({
      host: cutterHost,
      wholeLabel: TEXTS.tiktokWhole,
      cutter: () => cutter,
      // The cutter has its own player; two would fight.
      onToggle: (open) => {
        if (open) player?.pause();
        playerRow.hidden = open || !player;
        renderComing();
      },
    });
    mixWatchers.add(renderSaved);
    onCleanup(key, () => mixWatchers.delete(renderSaved));

    const isTiktok = (value: string): boolean => /^https?:\/\/([a-z0-9-]+\.)*tiktok\.com\//i.test(value.trim());

    /** Bumped for every link, so a late answer about an old one is dropped. */
    let seq = 0;
    /** The link whose sound is loading now. */
    let loading: string | null = null;
    let autoTimer: number | undefined;

    /** Forgets the loaded (or loading) sound: a new link, a retry, the editor thrown away. */
    const dropSound = (): void => {
      seq++;
      loading = null;
      window.clearTimeout(autoTimer);
      cutter?.destroy();
      cutter = null;
      player?.destroy();
      player = null;
      playerRow.innerHTML = '';
      playerRow.hidden = true;
      cutterHost.innerHTML = '';
      setComing(null);
      locked.hidden = true;
      loadButton.hidden = true;
      openedForOver = false;
      partPick.reset(false);
    };
    onCleanup(key, dropSound);

    /** The cutter over the sound's length, the moment the server knows it — folded until "Iseci deo". */
    const ensureCutter = (durationMs: number): void => {
      if (cutter || !(durationMs > 0)) return;
      const sel = selections.get(key);
      const opening = sel?.fromMs != null || sel?.toMs != null ? { fromMs: sel.fromMs, toMs: sel.toMs } : undefined;
      cutter = createPendingCutter({
        pending: { durationMs },
        transport: null,
        wholeSong: true,
        wholeLabel: TEXTS.tiktokWhole,
        maxMs: rules.limitMs,
        fitToMax: !opening,
        initialRange: opening,
        initialEffects: sel,
        fadeChoicesMs: config.fadeChoicesMs,
        defaultFadeMs: config.defaultFadeMs,
        onRange: (fromMs, toMs) => patch(key, { fromMs, toMs }),
        onEffects: (effects: AudioEffects) => patch(key, chosenEffects(effects)),
        onPlay,
      });
      if (!cutter) return;
      cutterHost.appendChild(cutter.element);
      // A part chosen before (a restored answer) opens cut — and so does a
      // sound the arrangement has no room for whole (see renderSaved).
      partPick.reset(Boolean(opening));
      renderSaved();
    };

    const start = (input: string): void => {
      window.clearTimeout(autoTimer);
      if (loading === input) return;
      if (!isTiktok(input)) {
        hint.hidden = false;
        hint.textContent = TEXTS.tiktokNotLink;
        return;
      }
      dropSound();
      const run = seq;
      loading = input;
      hint.hidden = true;
      setSaved(TEXTS.tiktokSaved);
      setComing({ text: TEXTS.tiktokComing, percent: 3 });

      void (async () => {
        try {
          const { id: prepId } = await api.prepareAudio(input);
          for (let attempt = 0; attempt < 120; attempt++) {
            if (attempt > 0) await sleep(2000);
            if (run !== seq) return;
            const statusRow = await api.prepareStatus(prepId);
            if (run !== seq) return;
            if (statusRow.status === 'error') {
              // Locked at the source (private, removed, closed to every
              // address we tried): no later download will do better, so the
              // link is not an answer — the shopper is asked for another.
              if (prepLocked(statusRow)) {
                setComing(null);
                setSaved('');
                cutter?.destroy();
                cutter = null;
                cutterHost.innerHTML = '';
                partPick.sync();
                locked.hidden = false;
                clearKey(key);
                return;
              }
              throw new Error('failed');
            }
            // Named and measured while it still loads: its length counts in
            // the total from now on, and the cutter is ready behind "Iseci deo".
            const measured = statusRow.durationMs && statusRow.durationMs > 0 ? statusRow.durationMs : 0;
            const named = statusRow.title ? `TikTok: ${statusRow.title}` : undefined;
            const sel = selections.get(key);
            if (sel && measured && (sel.durationMs !== measured || (named && sel.title !== named))) {
              patch(key, { durationMs: measured, ...(named ? { title: named } : {}) });
            }
            if (measured) ensureCutter(measured);
            if (statusRow.status === 'done' && statusRow.url && measured) {
              setComing(null);
              patch(key, {
                url: statusRow.sourceUrl ?? input,
                storageKey: statusRow.storageKey ?? undefined,
                title: named ?? 'TikTok zvuk',
                durationMs: measured,
                previewUrl: statusRow.url,
              });
              player = makePlayer(statusRow.url, { knownMs: measured });
              playerRow.appendChild(player.element);
              playerRow.hidden = partPick.isOpen();
              const ok = cutter
                ? await cutter.setAudio({ peaks: statusRow.peaks, durationMs: measured, audioUrl: statusRow.url })
                : false;
              if (run !== seq) return;
              setSaved(TEXTS.tiktokReady);
              if (!ok) cutter?.setStatus(null);
              return;
            }
            const percent = Number.isFinite(statusRow.progress) ? Math.max(3, statusRow.progress) : 10;
            // Said once: on the panel while the cutter is folded, under the wave once it is open.
            setComing({ text: TEXTS.tiktokComing, percent });
            cutter?.setStatus({ text: TEXTS.tiktokComing, percent, tone: 'busy' });
          }
          throw new Error('timeout');
        } catch {
          if (run !== seq) return;
          // Not loaded here — the link is still the answer, fetched after the order.
          setComing(null);
          setSaved(`${TEXTS.tiktokLater}. ${TEXTS.tiktokLaterNote}`);
          cutter?.setStatus({ text: TEXTS.tiktokLaterNote, tone: 'note' });
          loadButton.hidden = false;
        } finally {
          if (run === seq) loading = null;
        }
      })();
    };

    linkInput.addEventListener('input', () => {
      const value = linkInput.value.trim();
      dropSound();
      setSaved('');
      hint.hidden = false;
      hint.textContent = TEXTS.tiktokHint;
      if (!value) {
        clearKey(key);
        return;
      }
      const isLink = /^https?:\/\//i.test(value);
      patch(key, {
        url: isLink ? value : undefined,
        storageKey: undefined,
        previewUrl: undefined,
        durationMs: undefined,
        title: 'TikTok zvuk',
        fromMs: undefined,
        toMs: undefined,
      });
      if (isTiktok(value)) {
        // The answer now; its sound a moment later, once the link stops changing.
        hint.hidden = true;
        setSaved(TEXTS.tiktokSaved);
        autoTimer = window.setTimeout(() => start(value), 400);
      } else if (isLink) {
        autoTimer = window.setTimeout(() => {
          hint.textContent = TEXTS.tiktokNotLink;
        }, 400);
      }
    });
    loadButton.addEventListener('click', () => start(linkInput.value.trim()));
    linkInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        start(linkInput.value.trim());
      }
    });

    // A restored link: saved before its sound loaded, it loads now as a fresh
    // paste would (with its part, if one was chosen); one saved with its file
    // plays from the kept copy above.
    const kept = selections.get(key);
    if (kept?.url) {
      linkInput.value = kept.url;
      if (!kept.storageKey && isTiktok(kept.url)) {
        hint.hidden = true;
        setSaved(TEXTS.tiktokSaved);
        autoTimer = window.setTimeout(() => start(kept.url!), 400);
      }
    }

    panel.append(keptBlock('tiktok', key), row, hint, savedLine, coming.element, locked, playerRow, partPick.element, cutterHost);
  }

  // ── Own files ──────────────────────────────────────────────────────────────
  function buildUploadPanel(panel: HTMLElement, state: Panel): void {
    const maxFiles = Math.max(1, Math.min(10, config.maxUploadFiles ?? 5));
    let nextIndex = 0;
    const rows = new Map<string, HTMLElement>();

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'audio/*';
    fileInput.multiple = maxFiles > 1;
    fileInput.hidden = true;
    const drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'pz-drop';
    drop.addEventListener('click', () => fileInput.click());
    const status = document.createElement('p');
    status.className = 'pz-hint';
    const cards = document.createElement('div');
    cards.className = 'pz-upload-list';

    const refreshDrop = (): void => {
      drop.hidden = rows.size >= maxFiles;
      drop.innerHTML = `${UPLOAD_ICON}<span>${rows.size > 0 ? TEXTS.addMoreAudio : TEXTS.chooseAudio}</span>`;
    };

    const removeKey = (key: string): void => {
      rows.get(key)?.remove();
      rows.delete(key);
      selections.delete(key);
      refreshDrop();
      emit();
    };
    state.removeKey = removeKey;

    /** One file's row: its name, a player once it is saved, and its remove button. */
    const makeRow = (key: string, name: string): { wrap: HTMLElement; card: HTMLElement; nameEl: HTMLElement; body: HTMLElement } => {
      const card = document.createElement('div');
      card.className = 'pz-file-card pz-audio-file';
      const icon = document.createElement('span');
      icon.className = 'pz-file-icon';
      icon.innerHTML = ICONS.music;
      const nameEl = document.createElement('span');
      nameEl.className = 'name';
      nameEl.textContent = name;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'x';
      remove.innerHTML = ICONS.close;
      remove.title = TEXTS.removeFile;
      remove.setAttribute('aria-label', TEXTS.removeFile);
      remove.addEventListener('click', () => removeKey(key));
      card.append(icon, nameEl, remove);
      const body = document.createElement('div');
      const wrap = document.createElement('div');
      wrap.className = 'pz-upload-item';
      wrap.append(card, body);
      cards.appendChild(wrap);
      rows.set(key, wrap);
      return { wrap, card, nameEl, body };
    };

    const addFile = async (file: File): Promise<void> => {
      if (rows.size >= maxFiles) throw new Error(`Najviše ${maxFiles} fajlova u ovom polju.`);
      const key = `upload:${nextIndex++}`;
      const { card, nameEl, body } = makeRow(key, file.name);
      refreshDrop();
      card.classList.add('is-busy');
      const lengthKnown = measure(file);
      await api
        .upload(file, file.name)
        .then(async (result) => {
          card.classList.remove('is-busy');
          if (!rows.has(key)) throw new Error('Prenos je otkazan u obrascu.');
          const localUrl = URL.createObjectURL(file);
          onCleanup('upload', () => URL.revokeObjectURL(localUrl));
          const durationMs = await lengthKnown;
          if (!rows.has(key)) throw new Error('Prenos je otkazan u obrascu.');
          patch(key, { storageKey: result.storageKey, title: file.name, previewUrl: localUrl, durationMs });
          body.appendChild(soundRow('upload', key, localUrl, { knownMs: durationMs, blob: file, fallback: result.url }));
        })
        .catch((err: Error) => {
          card.classList.remove('is-busy');
          // The upload's own words for a refused file speak of pictures.
          const why = err instanceof UploadError && err.status === 415 ? TEXTS.fileUnsupported : err.message;
          nameEl.textContent = `${file.name} — ${why}`;
          card.classList.add('is-error');
          rows.delete(key);
          refreshDrop();
          throw new Error(why);
        });
    };
    state.importFile = addFile;

    fileInput.addEventListener('change', () => {
      status.textContent = '';
      for (const file of [...(fileInput.files ?? [])]) void addFile(file).catch(err => { status.textContent = err.message; });
      fileInput.value = '';
    });

    // Files restored from a draft or the cart.
    for (const [key, sel] of selections) {
      if (kindOf(key) !== 'upload' || !hasSound(sel)) continue;
      nextIndex = Math.max(nextIndex, Number(key.split(':')[1] ?? 0) + 1);
      const { body } = makeRow(key, sel.title ?? 'Zvuk');
      if (sel.storageKey) body.appendChild(soundRow('upload', key, fileUrl(sel.storageKey), { knownMs: sel.durationMs }));
    }
    refreshDrop();

    const hint = document.createElement('p');
    hint.className = 'pz-hint';
    hint.textContent =
      maxFiles > 1
        ? `Možeš da otpremiš do ${maxFiles} fajlova — svaki možeš posebno da isečeš.`
        : 'Ako ne može da se otpremi, konvertuj fajl u MP3 pa pokušaj ponovo.';

    panel.append(drop, fileInput, hint, cards, status);
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A twentieth of a second of silence, as a WAV. Played inside the "Preslušaj
 * sve" tap so the one reused audio element counts as started by the shopper,
 * and a phone lets the following sounds play on their own.
 */
const SILENCE = (() => {
  const samples = 400;
  const bytes = new Uint8Array(44 + samples * 2);
  const view = new DataView(bytes.buffer);
  const write = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i++) bytes[at + i] = text.charCodeAt(i);
  };
  write(0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 8000, true);
  view.setUint32(28, 16000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  write(36, 'data');
  view.setUint32(40, samples * 2, true);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${typeof btoa === 'function' ? btoa(binary) : ''}`;
})();
